/**
 * Elite Batch 20 Part 4 — Workflow Templates 1908–1927
 * Support ticket QA escalation, support ticket expansion signal, tech stack
 * personalization, tech-stack change outreach, testimonial personalization match,
 * training upsell trigger, trial user expansion, trust center visitor signal,
 * Outgrow Forms lead classification, UTM visitor qualification pipeline, unsubscribe
 * suppression sync, viral post comment scraper, voice-call task creator,
 * warm-intro mapping, webhook retry with backoff, webinar attendee AE brief,
 * webinar page visitor watchlist, website-intent outreach, weekly ops digest
 * generator, and weekly pipeline digest workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 20 PART 4 — TEMPLATES 1908–1927
// =============================================================================

export const ELITE_BATCH20_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1908. SUPPORT TICKET QA ESCALATION (8 nodes)
  // ===================================================================

  "Support Ticket QA Escalation": {
    summary:
      "Fires on a ticket response webhook to fetch the full response context, score agent response quality with AI, check sensitivity flags with runCode, branch by quality+sensitivity, generate coaching feedback with AI, escalate to supervisor via Slack, and log QA results to Sheets.",
    nodes: [
      trigger("Webhook: Ticket response submitted", "webhook_app", {
        source: "helpdesk",
        event: "ticket.response_submitted",
        howItStarts:
          "Fires whenever a support agent submits a response to a customer ticket, triggering an automated QA review that scores quality, checks sensitivity, generates coaching, and escalates issues to supervisors",
        setup:
          "Connect your helpdesk (Zendesk, Freshdesk, Intercom) to fire webhooks on agent response events. Set OpenAI API key for quality scoring and coaching. Configure Slack webhook for supervisor escalation. Set Google Sheets credentials for QA tracking.",
        inputFields: [],
      }),

      // 2. Fetch ticket response context runCode
      n("2", "runCode", "Fetch Ticket Response Context", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook payload and extract ticket response context
const payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload;
const ticketId = payload.ticket_id || payload.id || payload.ticket?.id || "unknown";
const agentName = payload.agent_name || payload.agent?.name || payload.assignee?.name || "Unknown Agent";
const agentEmail = payload.agent_email || payload.agent?.email || payload.assignee?.email || "";
const customerMessage = payload.customer_message || payload.ticket?.last_customer_message || payload.description || "";
const agentResponse = payload.agent_response || payload.response?.body || payload.comment?.body || "";
const ticketSubject = payload.subject || payload.ticket?.subject || "";
const ticketPriority = payload.priority || payload.ticket?.priority || "normal";
const ticketTags = Array.isArray(payload.tags) ? payload.tags.join(", ") : (payload.tags || "");
const ticketCategory = payload.category || payload.ticket?.category || payload.type || "general";
const responseTime = payload.response_time_minutes || payload.metrics?.reply_time || 0;
return { ticket_id: ticketId, agent_name: agentName, agent_email: agentEmail, customer_message: customerMessage, agent_response: agentResponse, ticket_subject: ticketSubject, ticket_priority: ticketPriority, ticket_tags: ticketTags, ticket_category: ticketCategory, response_time_minutes: responseTime };`,
          outputVariables: [
            { name: "ticket_id", type: "string" },
            { name: "agent_name", type: "string" },
            { name: "agent_email", type: "string" },
            { name: "customer_message", type: "string" },
            { name: "agent_response", type: "string" },
            { name: "ticket_subject", type: "string" },
            { name: "ticket_priority", type: "string" },
            { name: "ticket_tags", type: "string" },
            { name: "ticket_category", type: "string" },
            { name: "response_time_minutes", type: "number" },
          ],
        },
      }),

      // 3. AI score agent response quality
      aiNode(
        "3",
        "AI Score Agent Response Quality",
        520,
        "You are a support QA analyst. Given a customer message and the agent's response, score the agent's response on: accuracy (0-25), empathy (0-25), completeness (0-25), and professionalism (0-25). Identify any policy violations, tone issues, or missing information. Respond JSON: {total_score: number, accuracy_score: number, empathy_score: number, completeness_score: number, professionalism_score: number, issues_found: string, improvement_areas: string, overall_rating: string}",
        "Ticket subject: {ticket_subject}. Priority: {ticket_priority}. Category: {ticket_category}. Customer message: {customer_message}. Agent response: {agent_response}. Response time: {response_time_minutes} minutes. Score the quality of this agent response.",
        [
          { field: "total_score", as: "qa_total_score", type: "number" },
          { field: "accuracy_score", as: "accuracy_score", type: "number" },
          { field: "empathy_score", as: "empathy_score", type: "number" },
          {
            field: "completeness_score",
            as: "completeness_score",
            type: "number",
          },
          {
            field: "professionalism_score",
            as: "professionalism_score",
            type: "number",
          },
          { field: "issues_found", as: "issues_found", type: "string" },
          {
            field: "improvement_areas",
            as: "improvement_areas",
            type: "string",
          },
          { field: "overall_rating", as: "overall_rating", type: "string" },
        ],
      ),

      // 4. Check sensitivity flags runCode
      n("4", "runCode", "Check Sensitivity Flags", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check for sensitive content, VIP customers, and escalation triggers
const tags = (vars.ticket_tags || "").toLowerCase();
const priority = (vars.ticket_priority || "").toLowerCase();
const category = (vars.ticket_category || "").toLowerCase();
const qaScore = parseInt(vars.qa_total_score) || 0;
const issues = (vars.issues_found || "").toLowerCase();
let sensitivity_level = "normal";
const flags = [];
if (tags.includes("vip") || tags.includes("enterprise") || tags.includes("strategic")) { flags.push("VIP_customer"); sensitivity_level = "high"; }
if (priority === "urgent" || priority === "critical" || priority === "high") { flags.push("high_priority_ticket"); if (sensitivity_level !== "high") sensitivity_level = "elevated"; }
if (category.includes("billing") || category.includes("legal") || category.includes("security")) { flags.push("sensitive_category"); sensitivity_level = "high"; }
if (issues.includes("policy violation") || issues.includes("inappropriate") || issues.includes("incorrect")) { flags.push("policy_concern"); sensitivity_level = "high"; }
if (qaScore < 50) { flags.push("low_quality_score"); if (sensitivity_level === "normal") sensitivity_level = "elevated"; }
const needs_escalation = sensitivity_level === "high" || qaScore < 40;
return { sensitivity_level: sensitivity_level, sensitivity_flags: flags.join(", ") || "none", needs_escalation: needs_escalation ? "true" : "false", flag_count: flags.length };`,
          outputVariables: [
            { name: "sensitivity_level", type: "string" },
            { name: "sensitivity_flags", type: "string" },
            { name: "needs_escalation", type: "string" },
            { name: "flag_count", type: "number" },
          ],
        },
      }),

      // 5. Branch by quality + sensitivity condition
      n("5", "condition", "Branch by Quality and Sensitivity", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "needs_escalation", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. AI generate coaching feedback
      aiNode(
        "6",
        "AI Generate Coaching Feedback",
        1240,
        "You are a support team lead. Given the QA scores, issues found, and improvement areas for an agent's response, write constructive coaching feedback. Be specific about what was done well and what needs improvement. Include actionable suggestions. Keep encouraging but direct. Respond JSON: {coaching_summary: string, strengths: string, action_items: string, recommended_training: string}",
        "Agent: {agent_name}. QA score: {qa_total_score}/100 (accuracy: {accuracy_score}, empathy: {empathy_score}, completeness: {completeness_score}, professionalism: {professionalism_score}). Issues: {issues_found}. Improvement areas: {improvement_areas}. Sensitivity: {sensitivity_level}. Flags: {sensitivity_flags}. Write coaching feedback.",
        [
          { field: "coaching_summary", as: "coaching_summary", type: "string" },
          { field: "strengths", as: "agent_strengths", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          {
            field: "recommended_training",
            as: "recommended_training",
            type: "string",
          },
        ],
      ),

      // 7. Escalate to supervisor Slack
      slackNode(
        "7",
        "Escalate to Supervisor Slack",
        1480,
        ":rotating_light: *QA Escalation — Ticket #{ticket_id}*\n*Agent:* {agent_name}\n*QA Score:* {qa_total_score}/100 | *Rating:* {overall_rating}\n*Sensitivity:* {sensitivity_level} | *Flags:* {sensitivity_flags}\n*Issues:* {issues_found}\n*Coaching:* {coaching_summary}\n*Action Items:* {action_items}\n*Subject:* {ticket_subject}",
      ),

      // 8. Log QA results Sheets
      sheetsNode(
        "8",
        "Log QA Results to Sheets",
        1720,
        "QA_Log",
        '[["=NOW()","{ticket_id}","{agent_name}","{qa_total_score}","{accuracy_score}","{empathy_score}","{completeness_score}","{professionalism_score}","{overall_rating}","{sensitivity_level}","{sensitivity_flags}","{issues_found}","{needs_escalation}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1909. SUPPORT TICKET TO EXPANSION SIGNAL (8 nodes)
  // ===================================================================

  "Support Ticket to Expansion Signal": {
    summary:
      "Fires on a ticket event webhook to analyze expansion signals with AI, fetch customer account data from HubSpot, score expansion opportunity with runCode, generate an expansion brief with AI, branch by signal strength, route to CSM via Slack, and create an expansion opportunity in HubSpot.",
    nodes: [
      trigger("Webhook: Support ticket event", "webhook_app", {
        source: "helpdesk",
        event: "ticket.created_or_updated",
        howItStarts:
          "Fires whenever a support ticket is created or updated, analyzing the conversation for expansion signals like feature requests, usage growth inquiries, or additional seat needs, then routes qualified expansion opportunities to the CSM team",
        setup:
          "Connect your helpdesk to fire webhooks on ticket events. Set OpenAI API key for signal analysis and brief generation. Provide HubSpot token for account data and opportunity creation. Configure Slack webhook for CSM routing.",
        inputFields: [],
      }),

      // 2. AI analyze expansion signals
      aiNode(
        "2",
        "AI Analyze Expansion Signals",
        280,
        "You are a customer success analyst. Given a support ticket payload, identify any expansion signals: feature requests suggesting they need a higher tier, questions about additional seats or users, inquiries about advanced features, requests for integrations available on premium plans, mentions of growing team or usage. Find the contact email and company regardless of payload format. Respond JSON: {contact_email: string, company_name: string, ticket_id: string, ticket_subject: string, signal_type: string, signal_description: string, expansion_potential: string, current_pain_point: string, suggested_upgrade_path: string, signal_confidence: number}",
        "Analyze this support ticket payload for expansion signals: {webhook_payload}",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "ticket_id", as: "ticket_id", type: "string" },
          { field: "ticket_subject", as: "ticket_subject", type: "string" },
          { field: "signal_type", as: "signal_type", type: "string" },
          {
            field: "signal_description",
            as: "signal_description",
            type: "string",
          },
          {
            field: "expansion_potential",
            as: "expansion_potential",
            type: "string",
          },
          {
            field: "current_pain_point",
            as: "current_pain_point",
            type: "string",
          },
          {
            field: "suggested_upgrade_path",
            as: "suggested_upgrade_path",
            type: "string",
          },
          {
            field: "signal_confidence",
            as: "signal_confidence",
            type: "number",
          },
        ],
      ),

      // 3. Fetch customer account data HubSpot
      hubspotGetNode(
        "3",
        "Fetch Customer Account Data HubSpot",
        520,
        "crm/v3/objects/companies/search",
        [
          {
            name: "results[0].id",
            customName: "hubspot_company_id",
            type: "string",
          },
          {
            name: "results[0].properties.annualrevenue",
            customName: "annual_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "results[0].properties.hs_object_id",
            customName: "company_object_id",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_contacts",
            customName: "contact_count",
            type: "string",
          },
          {
            name: "results[0].properties.industry",
            customName: "industry",
            type: "string",
          },
        ],
      ),

      // 4. Score expansion opportunity runCode
      n("4", "runCode", "Score Expansion Opportunity", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score the expansion opportunity based on signals and account data
const confidence = parseInt(vars.signal_confidence) || 0;
const signalType = (vars.signal_type || "").toLowerCase();
const potential = (vars.expansion_potential || "").toLowerCase();
const revenue = parseInt(vars.annual_revenue) || 0;
const contacts = parseInt(vars.contact_count) || 0;
let score = 0;
// Signal confidence (0-30)
score += Math.min(30, Math.round(confidence * 0.3));
// Signal type weighting (0-25)
if (signalType.includes("seat") || signalType.includes("user")) score += 25;
else if (signalType.includes("feature") || signalType.includes("upgrade")) score += 20;
else if (signalType.includes("integration") || signalType.includes("api")) score += 18;
else if (signalType.includes("usage") || signalType.includes("growth")) score += 15;
else score += 8;
// Account size (0-25)
if (revenue > 1000000) score += 25;
else if (revenue > 500000) score += 20;
else if (revenue > 100000) score += 15;
else score += 8;
// Expansion potential (0-20)
if (potential.includes("high")) score += 20;
else if (potential.includes("medium")) score += 12;
else score += 5;
const finalScore = Math.min(100, score);
const strength = finalScore >= 75 ? "strong" : finalScore >= 50 ? "moderate" : "weak";
return { expansion_score: finalScore, signal_strength: strength, is_qualified: finalScore >= 50 ? "true" : "false" };`,
          outputVariables: [
            { name: "expansion_score", type: "number" },
            { name: "signal_strength", type: "string" },
            { name: "is_qualified", type: "string" },
          ],
        },
      }),

      // 5. AI expansion context brief
      aiNode(
        "5",
        "AI Generate Expansion Brief",
        1000,
        "You are a customer success strategist. Given the expansion signal analysis, account data, and score, write a concise expansion opportunity brief for the CSM. Include the signal, recommended next steps, talk track suggestions, and potential revenue impact. Respond JSON: {brief_summary: string, recommended_action: string, talk_track: string, estimated_expansion_value: string, urgency: string}",
        "Company: {company_name}. Signal type: {signal_type}. Description: {signal_description}. Pain point: {current_pain_point}. Suggested upgrade: {suggested_upgrade_path}. Expansion score: {expansion_score}/100. Strength: {signal_strength}. Revenue: {annual_revenue}. Industry: {industry}. Contacts: {contact_count}. Write the expansion brief.",
        [
          { field: "brief_summary", as: "brief_summary", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "talk_track", as: "talk_track", type: "string" },
          {
            field: "estimated_expansion_value",
            as: "estimated_expansion_value",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 6. Branch by signal strength condition
      n("6", "condition", "Branch by Expansion Signal Strength", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "signal_strength",
              operator: "equals",
              value: "strong",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Route to CSM Slack
      slackNode(
        "7",
        "Route Expansion Signal to CSM Slack",
        1480,
        ":chart_with_upwards_trend: *Expansion Signal Detected — {company_name}*\n*Signal:* {signal_type} | *Strength:* {signal_strength} ({expansion_score}/100)\n*Description:* {signal_description}\n*Ticket:* #{ticket_id} — {ticket_subject}\n*Brief:* {brief_summary}\n*Action:* {recommended_action}\n*Talk Track:* {talk_track}\n*Est. Value:* {estimated_expansion_value} | *Urgency:* {urgency}",
      ),

      // 8. Create expansion opportunity HubSpot
      hubspotPostNode(
        "8",
        "Create Expansion Opportunity HubSpot",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Expansion: {company_name} — {signal_type}","pipeline":"default","dealstage":"qualifiedtobuy","amount":"{estimated_expansion_value}","description":"{brief_summary}\\nSignal: {signal_description}\\nRecommended: {recommended_action}","hs_priority":"{urgency}"}',
          },
        ],
        [
          { name: "id", customName: "deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1910. TECH STACK PERSONALIZATION (8 nodes)
  // ===================================================================

  "Tech Stack Personalization": {
    summary:
      "Fires on a target account webhook to detect tech stack via BuiltWith API, cross-reference with Wappalyzer API, map integration opportunities with AI, generate an integration-focused pitch with AI, branch by stack complexity, send personalized outreach email, and log tech stack data to HubSpot.",
    nodes: [
      trigger("Webhook: Target account identified", "webhook_app", {
        source: "prospecting",
        event: "account.target_identified",
        howItStarts:
          "Fires when a target account is identified for outreach, detecting their tech stack via BuiltWith and Wappalyzer, mapping integration opportunities, and generating hyper-personalized outreach based on exact technologies in use",
        setup:
          "Set BuiltWith API key for tech detection. Set Wappalyzer API key for cross-reference. Set OpenAI API key for opportunity mapping and pitch generation. Provide HubSpot token for CRM logging. Configure SMTP for outreach.",
        inputFields: [],
      }),

      // 2. Detect tech stack BuiltWith API
      n("2", "hitApi", "Detect Tech Stack via BuiltWith", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.builtwith.com/free1/api.json?KEY={builtwith_key}&LOOKUP={target_domain}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "Results[0].Result.Paths[0].Technologies",
              customName: "builtwith_technologies",
              type: "string",
            },
            {
              name: "Results[0].Result.Paths[0].Domain",
              customName: "target_domain",
              type: "string",
            },
            {
              name: "Results[0].Result.IsDB",
              customName: "builtwith_found",
              type: "string",
            },
          ],
        },
      }),

      // 3. Cross-reference Wappalyzer API
      n("3", "hitApi", "Cross-Reference Tech via Wappalyzer", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.wappalyzer.com/v2/lookup/?urls={target_domain}",
          headers: [
            { key: "x-api-key", value: "{wappalyzer_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "[0].technologies",
              customName: "wappalyzer_technologies",
              type: "string",
            },
            { name: "[0].url", customName: "wappalyzer_url", type: "string" },
          ],
        },
      }),

      // 4. AI map integration opportunities
      aiNode(
        "4",
        "AI Map Integration Opportunities",
        760,
        "You are a solutions engineer. Given two tech stack detection results from BuiltWith and Wappalyzer for a prospect domain, consolidate the findings and identify specific integration opportunities with your platform. Map each detected technology to a potential integration point, displacement opportunity, or complementary use case. Respond JSON: {consolidated_stack: string, crm_detected: string, email_platform: string, analytics_tools: string, integration_opportunities: string, displacement_targets: string, complementary_tools: string, stack_complexity: string, total_technologies: number}",
        "Domain: {target_domain}. BuiltWith results: {builtwith_technologies}. Wappalyzer results: {wappalyzer_technologies}. Identify integration opportunities and displacement targets.",
        [
          {
            field: "consolidated_stack",
            as: "consolidated_stack",
            type: "string",
          },
          { field: "crm_detected", as: "crm_detected", type: "string" },
          { field: "email_platform", as: "email_platform", type: "string" },
          { field: "analytics_tools", as: "analytics_tools", type: "string" },
          {
            field: "integration_opportunities",
            as: "integration_opportunities",
            type: "string",
          },
          {
            field: "displacement_targets",
            as: "displacement_targets",
            type: "string",
          },
          {
            field: "complementary_tools",
            as: "complementary_tools",
            type: "string",
          },
          { field: "stack_complexity", as: "stack_complexity", type: "string" },
          {
            field: "total_technologies",
            as: "total_technologies",
            type: "number",
          },
        ],
      ),

      // 5. AI integration-focused pitch
      aiNode(
        "5",
        "AI Generate Integration-Focused Pitch",
        1000,
        "You are an outbound sales expert who specializes in tech-stack-aware messaging. Given the prospect's specific technologies and identified integration opportunities, write a highly personalized outreach email that references their exact stack. Mention specific integrations you offer, name the tools they use, and explain exactly how you connect. Under 150 words. Respond JSON: {subject: string, body: string, ps_line: string, integration_hook: string}",
        "Domain: {target_domain}. Stack: {consolidated_stack}. CRM: {crm_detected}. Email: {email_platform}. Analytics: {analytics_tools}. Integration opportunities: {integration_opportunities}. Displacement targets: {displacement_targets}. Complementary: {complementary_tools}. Write the stack-aware outreach.",
        [
          { field: "subject", as: "pitch_subject", type: "string" },
          { field: "body", as: "pitch_body", type: "string" },
          { field: "ps_line", as: "pitch_ps", type: "string" },
          { field: "integration_hook", as: "integration_hook", type: "string" },
        ],
      ),

      // 6. Branch by stack complexity condition
      n("6", "condition", "Branch by Stack Complexity", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "stack_complexity",
              operator: "equals",
              value: "complex",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Send outreach email
      emailNode(
        "7",
        "Send Tech-Stack Outreach Email",
        1480,
        "{contact_email}",
        "{pitch_subject}",
        "<p>{pitch_body}</p><p>P.S. {pitch_ps}</p>",
      ),

      // 8. Log tech stack CRM HubSpot
      hubspotPostNode(
        "8",
        "Log Tech Stack to HubSpot CRM",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{contact_email}","tech_stack":"{consolidated_stack}","crm_in_use":"{crm_detected}","email_platform":"{email_platform}","stack_complexity":"{stack_complexity}","integration_opportunities":"{integration_opportunities}","total_technologies":"{total_technologies}"}',
          },
        ],
        [
          { name: "id", customName: "contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "logged_email",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1911. TECH-STACK CHANGE OUTREACH (8 nodes)
  // ===================================================================

  "Tech-Stack Change Outreach": {
    summary:
      "Fires on a stack change webhook to validate change details with runCode, analyze the displacement opportunity with AI, fetch account context from HubSpot, generate change-aware outreach with AI, branch by change type, send a displacement email, and create a competitive intel record in HubSpot.",
    nodes: [
      trigger("Webhook: Tech stack change detected", "webhook_app", {
        source: "tech_monitoring",
        event: "stack.technology_changed",
        howItStarts:
          "Fires when a monitoring service detects a technology addition, removal, or switch at a target account, allowing immediate outreach that references the exact change and positions your solution as the natural next step",
        setup:
          "Connect your tech stack monitoring tool (BuiltWith Alerts, Slintel, or HG Insights) to fire webhooks on technology changes. Set OpenAI API key for analysis and outreach generation. Provide HubSpot token for account context and intel logging. Configure SMTP for outreach.",
        inputFields: [],
      }),

      // 2. Validate change details runCode
      n("2", "runCode", "Validate Stack Change Details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse and validate the technology change event
const payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload;
const domain = payload.domain || payload.company_domain || payload.url || "";
const companyName = payload.company_name || payload.company || domain.replace(/\\..+$/, "");
const changeType = payload.change_type || payload.event_type || "unknown";
const oldTech = payload.old_technology || payload.removed || payload.previous || "";
const newTech = payload.new_technology || payload.added || payload.current || "";
const category = payload.technology_category || payload.category || "unknown";
const detectedAt = payload.detected_at || payload.timestamp || new Date().toISOString();
const contactEmail = payload.contact_email || payload.email || "";
const isValid = domain && (oldTech || newTech) && changeType !== "unknown";
return { company_domain: domain, company_name: companyName, change_type: changeType, old_technology: oldTech, new_technology: newTech, tech_category: category, detected_at: detectedAt, contact_email: contactEmail, is_valid_change: isValid ? "true" : "false" };`,
          outputVariables: [
            { name: "company_domain", type: "string" },
            { name: "company_name", type: "string" },
            { name: "change_type", type: "string" },
            { name: "old_technology", type: "string" },
            { name: "new_technology", type: "string" },
            { name: "tech_category", type: "string" },
            { name: "detected_at", type: "string" },
            { name: "contact_email", type: "string" },
            { name: "is_valid_change", type: "string" },
          ],
        },
      }),

      // 3. AI analyze displacement opportunity
      aiNode(
        "3",
        "AI Analyze Displacement Opportunity",
        520,
        "You are a competitive intelligence analyst. Given a technology change event (addition, removal, or switch), analyze the displacement opportunity. Consider why a company would make this change, what pain points it reveals, and how your platform can capitalize. Respond JSON: {opportunity_type: string, pain_point_analysis: string, displacement_angle: string, competitive_position: string, timing_advantage: string, recommended_approach: string, urgency_level: string}",
        "Company: {company_name} ({company_domain}). Change type: {change_type}. Old tech: {old_technology}. New tech: {new_technology}. Category: {tech_category}. Detected: {detected_at}. Analyze the displacement opportunity.",
        [
          { field: "opportunity_type", as: "opportunity_type", type: "string" },
          {
            field: "pain_point_analysis",
            as: "pain_point_analysis",
            type: "string",
          },
          {
            field: "displacement_angle",
            as: "displacement_angle",
            type: "string",
          },
          {
            field: "competitive_position",
            as: "competitive_position",
            type: "string",
          },
          { field: "timing_advantage", as: "timing_advantage", type: "string" },
          {
            field: "recommended_approach",
            as: "recommended_approach",
            type: "string",
          },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      // 4. Fetch account context HubSpot
      hubspotGetNode(
        "4",
        "Fetch Account Context HubSpot",
        760,
        "crm/v3/objects/companies/search",
        [
          {
            name: "results[0].id",
            customName: "hubspot_company_id",
            type: "string",
          },
          {
            name: "results[0].properties.annualrevenue",
            customName: "annual_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.industry",
            customName: "industry",
            type: "string",
          },
          {
            name: "results[0].properties.numberofemployees",
            customName: "employee_count",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),

      // 5. AI change-aware outreach
      aiNode(
        "5",
        "AI Generate Change-Aware Outreach",
        1000,
        "You are a timing-savvy outbound sales expert. Write a personalized email that references the specific technology change at the prospect's company. Acknowledge the change naturally without being creepy, empathize with the transition challenges, and position your solution as the ideal complement or replacement. Under 150 words. Respond JSON: {subject: string, body: string, opening_hook: string}",
        "Company: {company_name}. Change: {change_type} — from {old_technology} to {new_technology} in {tech_category}. Pain points: {pain_point_analysis}. Displacement angle: {displacement_angle}. Approach: {recommended_approach}. Industry: {industry}. Size: {employee_count} employees. Write the change-aware outreach.",
        [
          { field: "subject", as: "outreach_subject", type: "string" },
          { field: "body", as: "outreach_body", type: "string" },
          { field: "opening_hook", as: "opening_hook", type: "string" },
        ],
      ),

      // 6. Branch by change type condition
      n("6", "condition", "Branch by Change Type", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "change_type", operator: "equals", value: "removal" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Send displacement email
      emailNode(
        "7",
        "Send Displacement Outreach Email",
        1480,
        "{contact_email}",
        "{outreach_subject}",
        "<p>{outreach_body}</p>",
      ),

      // 8. Create competitive intel record HubSpot
      hubspotPostNode(
        "8",
        "Create Competitive Intel Record HubSpot",
        1720,
        "crm/v3/objects/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"Tech Stack Change Intel — {company_name}\\nChange: {change_type} | Old: {old_technology} -> New: {new_technology}\\nCategory: {tech_category} | Detected: {detected_at}\\nOpportunity: {opportunity_type}\\nAngle: {displacement_angle}\\nUrgency: {urgency_level}\\nApproach: {recommended_approach}","hs_timestamp":"{detected_at}"}',
          },
        ],
        [{ name: "id", customName: "note_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1912. TESTIMONIAL PERSONALIZATION MATCH (7 nodes)
  // ===================================================================

  "Testimonial Personalization Match": {
    summary:
      "Fires on a proof request webhook to fetch prospect profile from HubSpot, search the testimonial database with runCode, match prospect to testimonial with AI, embed testimonial in outreach with AI, branch by match quality, and send the testimonial-enhanced email.",
    nodes: [
      trigger("Webhook: Social proof request", "webhook_app", {
        source: "sales_enablement",
        event: "proof.request_created",
        howItStarts:
          "Fires when a sales rep requests social proof for a prospect, automatically searching the testimonial database for the best industry/use-case match, embedding the testimonial naturally into personalized outreach, and delivering the ready-to-send email",
        setup:
          "Set OpenAI API key for matching and outreach generation. Provide HubSpot token for prospect profile lookup. Configure your testimonial database API or use the runCode node to search a structured JSON store. Configure SMTP for outreach.",
        inputFields: [],
      }),

      // 2. Fetch prospect profile HubSpot
      hubspotGetNode(
        "2",
        "Fetch Prospect Profile HubSpot",
        280,
        "crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,company,jobtitle,industry,lifecyclestage",
        [
          {
            name: "properties.email",
            customName: "prospect_email",
            type: "string",
          },
          {
            name: "properties.firstname",
            customName: "prospect_first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "prospect_last_name",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "prospect_company",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "prospect_title",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "prospect_industry",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
        ],
      ),

      // 3. Search testimonial database runCode
      n("3", "runCode", "Search Testimonial Database", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Search testimonial database for best match based on prospect profile
const industry = (vars.prospect_industry || "").toLowerCase();
const title = (vars.prospect_title || "").toLowerCase();
const company = (vars.prospect_company || "").toLowerCase();
// Simulated testimonial database — in production, this would query an API or database
const testimonials = [
  { id: "t1", name: "Sarah Chen", title: "VP Sales", company: "TechCorp", industry: "technology", quote: "Increased pipeline velocity by 40% in the first quarter", use_case: "sales_acceleration", metrics: "40% pipeline increase" },
  { id: "t2", name: "Mark Johnson", title: "CRO", company: "ScaleUp Inc", industry: "saas", quote: "Reduced sales cycle from 45 to 28 days with automated workflows", use_case: "cycle_reduction", metrics: "38% shorter cycles" },
  { id: "t3", name: "Lisa Park", title: "Director of Marketing", company: "GrowthCo", industry: "marketing", quote: "Our lead response time dropped from 4 hours to 12 minutes", use_case: "lead_response", metrics: "95% faster response" },
  { id: "t4", name: "David Kim", title: "Head of CS", company: "RetainPro", industry: "customer_success", quote: "Churn reduced by 22% through proactive engagement workflows", use_case: "churn_reduction", metrics: "22% less churn" },
  { id: "t5", name: "Rachel Torres", title: "CEO", company: "FinServ Solutions", industry: "financial_services", quote: "Compliance-ready automation saved us 200+ hours per month", use_case: "compliance_automation", metrics: "200+ hours saved monthly" },
];
// Score each testimonial for relevance
let bestMatch = testimonials[0];
let bestScore = 0;
for (const t of testimonials) {
  let score = 0;
  if (t.industry === industry || industry.includes(t.industry)) score += 40;
  if (title.includes(t.title.toLowerCase().split(" ").pop())) score += 30;
  score += 10; // base relevance
  if (score > bestScore) { bestScore = score; bestMatch = t; }
}
return { testimonial_name: bestMatch.name, testimonial_title: bestMatch.title, testimonial_company: bestMatch.company, testimonial_quote: bestMatch.quote, testimonial_use_case: bestMatch.use_case, testimonial_metrics: bestMatch.metrics, match_score: bestScore, testimonial_id: bestMatch.id };`,
          outputVariables: [
            { name: "testimonial_name", type: "string" },
            { name: "testimonial_title", type: "string" },
            { name: "testimonial_company", type: "string" },
            { name: "testimonial_quote", type: "string" },
            { name: "testimonial_use_case", type: "string" },
            { name: "testimonial_metrics", type: "string" },
            { name: "match_score", type: "number" },
            { name: "testimonial_id", type: "string" },
          ],
        },
      }),

      // 4. AI match prospect to testimonial
      aiNode(
        "4",
        "AI Match Prospect to Testimonial",
        760,
        "You are a sales enablement specialist. Given a prospect's profile and the best-matched testimonial, analyze the match quality and identify specific parallels between the prospect and the testimonial subject. Explain why this testimonial will resonate. Respond JSON: {match_quality: string, parallel_points: string, resonance_factors: string, positioning_recommendation: string, confidence_level: number}",
        'Prospect: {prospect_first_name} {prospect_last_name}, {prospect_title} at {prospect_company} ({prospect_industry}). Testimonial: {testimonial_name}, {testimonial_title} at {testimonial_company} — \\"{testimonial_quote}\\" (metrics: {testimonial_metrics}). Use case: {testimonial_use_case}. Match score: {match_score}. Analyze this match.',
        [
          { field: "match_quality", as: "match_quality", type: "string" },
          { field: "parallel_points", as: "parallel_points", type: "string" },
          {
            field: "resonance_factors",
            as: "resonance_factors",
            type: "string",
          },
          {
            field: "positioning_recommendation",
            as: "positioning_recommendation",
            type: "string",
          },
          { field: "confidence_level", as: "match_confidence", type: "number" },
        ],
      ),

      // 5. AI embed testimonial in outreach
      aiNode(
        "5",
        "AI Embed Testimonial in Outreach",
        1000,
        "You are a top-performing SDR who uses social proof masterfully. Write an outreach email that naturally weaves in the testimonial. Don't just paste the quote — connect it to the prospect's specific situation using the parallel points identified. The testimonial should feel like a natural part of the narrative, not an afterthought. Under 150 words. Respond JSON: {subject: string, body: string, testimonial_integration_style: string}",
        'Prospect: {prospect_first_name} {prospect_last_name}, {prospect_title} at {prospect_company}. Industry: {prospect_industry}. Testimonial from: {testimonial_name}, {testimonial_title} at {testimonial_company}. Quote: \\"{testimonial_quote}\\". Metrics: {testimonial_metrics}. Parallels: {parallel_points}. Resonance: {resonance_factors}. Positioning: {positioning_recommendation}. Write the testimonial-enhanced email.',
        [
          { field: "subject", as: "email_subject", type: "string" },
          { field: "body", as: "email_body", type: "string" },
          {
            field: "testimonial_integration_style",
            as: "integration_style",
            type: "string",
          },
        ],
      ),

      // 6. Branch by match quality condition
      n("6", "condition", "Branch by Testimonial Match Quality", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "match_quality", operator: "equals", value: "strong" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Send testimonial-enhanced email
      emailNode(
        "7",
        "Send Testimonial-Enhanced Email",
        1480,
        "{prospect_email}",
        "{email_subject}",
        "<p>{email_body}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1913. TRAINING UPSELL TRIGGER (8 nodes)
  // ===================================================================

  "Training Upsell Trigger": {
    summary:
      "Fires on a training signal webhook to analyze feature adoption gaps with AI, correlate support tickets with runCode, assess training need with AI, generate a training upsell pitch with AI, branch by training tier, send a training recommendation email, and create a training opportunity in HubSpot.",
    nodes: [
      trigger("Webhook: Training signal detected", "webhook_app", {
        source: "product_analytics",
        event: "user.training_signal",
        howItStarts:
          "Fires when product analytics detects a training signal — low feature adoption, repeated help doc visits, or support tickets about feature usage — then assesses the training need, generates a tier-appropriate upsell pitch, and creates a training opportunity",
        setup:
          "Connect product analytics (Pendo, Amplitude, or Mixpanel) to fire webhooks on training signals. Set OpenAI API key for adoption analysis and pitch generation. Provide HubSpot token for opportunity creation. Configure SMTP for outreach.",
        inputFields: [],
      }),

      // 2. AI analyze feature adoption gaps
      aiNode(
        "2",
        "AI Analyze Feature Adoption Gaps",
        280,
        "You are a product adoption analyst. Given a training signal webhook payload, identify which features the user is struggling with, their current adoption level, and the gap between current and ideal usage. Extract the user email and account regardless of format. Respond JSON: {user_email: string, user_name: string, account_name: string, underused_features: string, adoption_level: string, adoption_gap: string, struggle_indicators: string, feature_complexity: string, training_urgency: string}",
        "Analyze this training signal for feature adoption gaps: {webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "account_name", as: "account_name", type: "string" },
          {
            field: "underused_features",
            as: "underused_features",
            type: "string",
          },
          { field: "adoption_level", as: "adoption_level", type: "string" },
          { field: "adoption_gap", as: "adoption_gap", type: "string" },
          {
            field: "struggle_indicators",
            as: "struggle_indicators",
            type: "string",
          },
          {
            field: "feature_complexity",
            as: "feature_complexity",
            type: "string",
          },
          { field: "training_urgency", as: "training_urgency", type: "string" },
        ],
      ),

      // 3. Correlate support tickets runCode
      n("3", "runCode", "Correlate Support Tickets", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Correlate adoption gaps with support ticket patterns
const features = (vars.underused_features || "").toLowerCase();
const level = (vars.adoption_level || "").toLowerCase();
const gap = (vars.adoption_gap || "").toLowerCase();
const urgency = (vars.training_urgency || "").toLowerCase();
// Calculate training need score
let score = 0;
const featureList = features.split(",").map(f => f.trim()).filter(f => f);
const featureCount = featureList.length;
// More underused features = higher need
score += Math.min(30, featureCount * 10);
// Adoption level scoring
if (level.includes("low") || level.includes("minimal")) score += 30;
else if (level.includes("medium") || level.includes("partial")) score += 15;
else score += 5;
// Gap severity
if (gap.includes("significant") || gap.includes("large") || gap.includes("critical")) score += 25;
else if (gap.includes("moderate") || gap.includes("medium")) score += 15;
else score += 5;
// Urgency bonus
if (urgency.includes("high") || urgency.includes("critical")) score += 15;
else if (urgency.includes("medium")) score += 8;
const finalScore = Math.min(100, score);
const trainingTier = finalScore >= 75 ? "premium" : finalScore >= 45 ? "standard" : "self_serve";
return { training_need_score: finalScore, training_tier: trainingTier, feature_count: featureCount, needs_outreach: finalScore >= 35 ? "true" : "false" };`,
          outputVariables: [
            { name: "training_need_score", type: "number" },
            { name: "training_tier", type: "string" },
            { name: "feature_count", type: "number" },
            { name: "needs_outreach", type: "string" },
          ],
        },
      }),

      // 4. AI assess training need
      aiNode(
        "4",
        "AI Assess Training Need",
        760,
        "You are a customer success training specialist. Given the feature adoption analysis and training need score, assess what type of training would best serve this user. Recommend specific training formats (1:1 session, group webinar, self-paced course, custom workshop) and topics. Respond JSON: {training_recommendation: string, format: string, topics: string, expected_outcome: string, estimated_duration: string, value_proposition: string}",
        "User: {user_name} at {account_name}. Underused features: {underused_features}. Adoption level: {adoption_level}. Gap: {adoption_gap}. Struggle indicators: {struggle_indicators}. Feature complexity: {feature_complexity}. Training need score: {training_need_score}/100. Tier: {training_tier}. Feature count: {feature_count}. Assess the training need.",
        [
          {
            field: "training_recommendation",
            as: "training_recommendation",
            type: "string",
          },
          { field: "format", as: "training_format", type: "string" },
          { field: "topics", as: "training_topics", type: "string" },
          { field: "expected_outcome", as: "expected_outcome", type: "string" },
          {
            field: "estimated_duration",
            as: "estimated_duration",
            type: "string",
          },
          {
            field: "value_proposition",
            as: "training_value_prop",
            type: "string",
          },
        ],
      ),

      // 5. AI training upsell pitch
      aiNode(
        "5",
        "AI Generate Training Upsell Pitch",
        1000,
        "You are a customer success manager writing a helpful training outreach email. The tone should be genuinely helpful — not salesy. Reference the specific features the user is underutilizing, acknowledge the complexity, and present training as a way to unlock value they are already paying for. Mention the format, duration, and expected outcomes. Under 150 words. Respond JSON: {subject: string, body: string, cta: string}",
        "User: {user_name} at {account_name}. Email: {user_email}. Underused: {underused_features}. Recommendation: {training_recommendation}. Format: {training_format}. Topics: {training_topics}. Duration: {estimated_duration}. Outcome: {expected_outcome}. Value: {training_value_prop}. Tier: {training_tier}. Write the training outreach.",
        [
          { field: "subject", as: "email_subject", type: "string" },
          { field: "body", as: "email_body", type: "string" },
          { field: "cta", as: "email_cta", type: "string" },
        ],
      ),

      // 6. Branch by training tier condition
      n("6", "condition", "Branch by Training Tier", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "training_tier", operator: "equals", value: "premium" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Send training recommendation email
      emailNode(
        "7",
        "Send Training Recommendation Email",
        1480,
        "{user_email}",
        "{email_subject}",
        "<p>Hi {user_name},</p><p>{email_body}</p><p>{email_cta}</p>",
      ),

      // 8. Create training opportunity HubSpot
      hubspotPostNode(
        "8",
        "Create Training Opportunity HubSpot",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Training: {account_name} — {training_format}","pipeline":"default","dealstage":"appointmentscheduled","description":"Training need score: {training_need_score}/100\\nTier: {training_tier}\\nFeatures: {underused_features}\\nFormat: {training_format}\\nTopics: {training_topics}\\nDuration: {estimated_duration}"}',
          },
        ],
        [
          { name: "id", customName: "deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1914. TRIAL USER EXPANSION (8 nodes)
  // ===================================================================

  "Trial User Expansion": {
    summary:
      "Fires on a trial milestone webhook to fetch trial user profile from HubSpot, evaluate conversion readiness with runCode, generate a milestone-specific nudge with AI, branch by conversion score, send an upgrade nudge email, trigger an in-app message via API, and notify sales via Slack.",
    nodes: [
      trigger("Webhook: Trial milestone reached", "webhook_app", {
        source: "product_analytics",
        event: "trial.milestone_reached",
        howItStarts:
          "Fires when a trial user hits a key milestone (feature activation, usage threshold, team invite, or integration setup), evaluating their conversion readiness and delivering a personalized upgrade nudge through email, in-app messaging, and sales notification",
        setup:
          "Connect product analytics to fire webhooks on trial milestones. Provide HubSpot token for profile lookup. Set OpenAI API key for nudge generation. Configure in-app messaging API (Intercom, Pendo). Configure Slack webhook for sales alerts. Configure SMTP for outreach.",
        inputFields: [],
      }),

      // 2. Fetch trial user profile HubSpot
      hubspotGetNode(
        "2",
        "Fetch Trial User Profile HubSpot",
        280,
        "crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,company,jobtitle,hs_lifecyclestage,trial_start_date,plan_name",
        [
          {
            name: "properties.email",
            customName: "user_email",
            type: "string",
          },
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "company_name",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "job_title",
            type: "string",
          },
          {
            name: "properties.trial_start_date",
            customName: "trial_start_date",
            type: "string",
          },
          {
            name: "properties.plan_name",
            customName: "current_plan",
            type: "string",
          },
        ],
      ),

      // 3. Evaluate conversion readiness runCode
      n("3", "runCode", "Evaluate Conversion Readiness", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Evaluate trial user's conversion readiness based on milestone data
const payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload;
const milestone = payload.milestone || payload.event_name || "unknown";
const milestonesCompleted = parseInt(payload.milestones_completed || payload.total_milestones || "1") || 1;
const daysInTrial = parseInt(payload.days_in_trial || "0") || 0;
const activeUsers = parseInt(payload.active_users || payload.team_size || "1") || 1;
const featuresUsed = parseInt(payload.features_used || "0") || 0;
const integrationsSet = parseInt(payload.integrations_connected || "0") || 0;
// Score conversion readiness (0-100)
let score = 0;
// Milestone completion (0-25)
score += Math.min(25, milestonesCompleted * 5);
// Feature engagement (0-25)
score += Math.min(25, featuresUsed * 3);
// Team adoption (0-20)
if (activeUsers >= 5) score += 20;
else if (activeUsers >= 3) score += 15;
else if (activeUsers >= 2) score += 10;
else score += 5;
// Integration depth (0-15)
score += Math.min(15, integrationsSet * 5);
// Time pressure bonus (0-15) — closer to trial end = more urgent
if (daysInTrial >= 10) score += 15;
else if (daysInTrial >= 7) score += 10;
else score += 5;
const finalScore = Math.min(100, score);
const readiness = finalScore >= 70 ? "high" : finalScore >= 40 ? "medium" : "low";
const daysRemaining = Math.max(0, 14 - daysInTrial);
return { conversion_score: finalScore, conversion_readiness: readiness, milestone_name: milestone, milestones_completed: milestonesCompleted, days_remaining: daysRemaining, active_users: activeUsers, features_used: featuresUsed, integrations_connected: integrationsSet };`,
          outputVariables: [
            { name: "conversion_score", type: "number" },
            { name: "conversion_readiness", type: "string" },
            { name: "milestone_name", type: "string" },
            { name: "milestones_completed", type: "number" },
            { name: "days_remaining", type: "number" },
            { name: "active_users", type: "number" },
            { name: "features_used", type: "number" },
            { name: "integrations_connected", type: "number" },
          ],
        },
      }),

      // 4. AI milestone-specific nudge
      aiNode(
        "4",
        "AI Generate Milestone-Specific Nudge",
        760,
        "You are a trial conversion specialist. Given the milestone the user just hit and their conversion readiness data, write a personalized upgrade nudge email. Reference the specific milestone, acknowledge their progress, highlight what they would unlock on a paid plan, and create urgency around trial expiration. Be congratulatory but not pushy. Under 150 words. Respond JSON: {subject: string, body: string, upgrade_hook: string, in_app_message: string}",
        "User: {first_name} {last_name}, {job_title} at {company_name}. Milestone: {milestone_name}. Milestones completed: {milestones_completed}. Conversion score: {conversion_score}/100. Readiness: {conversion_readiness}. Days remaining: {days_remaining}. Active users: {active_users}. Features used: {features_used}. Integrations: {integrations_connected}. Current plan: {current_plan}. Write the milestone-specific upgrade nudge.",
        [
          { field: "subject", as: "nudge_subject", type: "string" },
          { field: "body", as: "nudge_body", type: "string" },
          { field: "upgrade_hook", as: "upgrade_hook", type: "string" },
          { field: "in_app_message", as: "in_app_message", type: "string" },
        ],
      ),

      // 5. Branch by conversion score condition
      n("5", "condition", "Branch by Conversion Score", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "conversion_readiness",
              operator: "equals",
              value: "high",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Send upgrade nudge email
      emailNode(
        "6",
        "Send Upgrade Nudge Email",
        1240,
        "{user_email}",
        "{nudge_subject}",
        "<p>Hi {first_name},</p><p>{nudge_body}</p><p>{upgrade_hook}</p>",
      ),

      // 7. Trigger in-app message API
      n("7", "hitApi", "Trigger In-App Upgrade Message", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.intercom.io/messages",
          headers: [
            { key: "Authorization", value: "Bearer {intercom_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "message_type", value: "inapp" },
            { key: "body", value: "{in_app_message}" },
            {
              key: "from",
              value: '{"type":"admin","id":"{intercom_admin_id}"}',
            },
            { key: "to", value: '{"type":"user","email":"{user_email}"}' },
          ],
          variables: [
            { name: "type", customName: "message_type", type: "string" },
            { name: "id", customName: "message_id", type: "string" },
          ],
        },
      }),

      // 8. Notify sales Slack
      slackNode(
        "8",
        "Notify Sales of Trial Conversion Signal",
        1720,
        ":rocket: *Trial Conversion Signal — {company_name}*\n*User:* {first_name} {last_name} ({job_title})\n*Milestone:* {milestone_name} | *Score:* {conversion_score}/100 ({conversion_readiness})\n*Days Remaining:* {days_remaining} | *Active Users:* {active_users}\n*Features Used:* {features_used} | *Integrations:* {integrations_connected}\n*Upgrade Hook:* {upgrade_hook}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1915. TRUST CENTER VISITOR SIGNAL (8 nodes)
  // ===================================================================

  "Trust Center Visitor Signal": {
    summary:
      "Fires on a trust center visit webhook to identify the visitor via Clearbit API, analyze compliance stage with AI, fetch account context from HubSpot, score security interest level with runCode, branch by compliance stage, alert the team via Slack, and create a security engagement task in HubSpot.",
    nodes: [
      trigger("Webhook: Trust center page visited", "webhook_app", {
        source: "website_tracking",
        event: "page.trust_center_visit",
        howItStarts:
          "Fires when a visitor views your trust center, security, or compliance pages, identifying the visitor, analyzing their likely compliance stage, scoring security interest, and routing high-interest visitors to the security team for proactive engagement",
        setup:
          "Connect your website tracking (Segment, Google Tag Manager, or Clearbit Reveal) to fire webhooks on trust center page visits. Set Clearbit API key for visitor identification. Set OpenAI API key for compliance analysis. Provide HubSpot token for account context and task creation. Configure Slack webhook for team alerts.",
        inputFields: [],
      }),

      // 2. Identify visitor Clearbit API
      n("2", "hitApi", "Identify Visitor via Clearbit", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://reveal.clearbit.com/v1/companies/find?domain={visitor_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "visitor_company", type: "string" },
            {
              name: "domain",
              customName: "visitor_domain_resolved",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "visitor_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "visitor_employee_count",
              type: "number",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "visitor_revenue",
              type: "string",
            },
            { name: "tech", customName: "visitor_tech_stack", type: "string" },
          ],
        },
      }),

      // 3. AI analyze compliance stage
      aiNode(
        "3",
        "AI Analyze Compliance Stage",
        520,
        "You are a security and compliance sales specialist. Given a visitor's company profile and the specific trust center pages they visited, analyze their likely compliance stage. Are they doing initial due diligence, vendor security review, procurement compliance check, or renewal audit? Identify the compliance frameworks most relevant to their industry. Respond JSON: {compliance_stage: string, likely_frameworks: string, security_concerns: string, buying_signal_strength: string, recommended_engagement: string, visitor_intent: string, deal_stage_estimate: string}",
        "Company: {visitor_company}. Industry: {visitor_industry}. Employees: {visitor_employee_count}. Revenue: {visitor_revenue}. Pages visited on trust center. Tech stack: {visitor_tech_stack}. Analyze their compliance stage and intent.",
        [
          { field: "compliance_stage", as: "compliance_stage", type: "string" },
          {
            field: "likely_frameworks",
            as: "likely_frameworks",
            type: "string",
          },
          {
            field: "security_concerns",
            as: "security_concerns",
            type: "string",
          },
          {
            field: "buying_signal_strength",
            as: "buying_signal_strength",
            type: "string",
          },
          {
            field: "recommended_engagement",
            as: "recommended_engagement",
            type: "string",
          },
          { field: "visitor_intent", as: "visitor_intent", type: "string" },
          {
            field: "deal_stage_estimate",
            as: "deal_stage_estimate",
            type: "string",
          },
        ],
      ),

      // 4. Fetch account context HubSpot
      hubspotGetNode(
        "4",
        "Fetch Account Context HubSpot",
        760,
        "crm/v3/objects/companies/search",
        [
          {
            name: "results[0].id",
            customName: "hubspot_company_id",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_updated",
            customName: "last_activity",
            type: "string",
          },
        ],
      ),

      // 5. Score security interest level runCode
      n("5", "runCode", "Score Security Interest Level", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score the security interest level based on all gathered signals
const stage = (vars.compliance_stage || "").toLowerCase();
const signal = (vars.buying_signal_strength || "").toLowerCase();
const intent = (vars.visitor_intent || "").toLowerCase();
const employees = parseInt(vars.visitor_employee_count) || 0;
const lifecycleStage = (vars.lifecycle_stage || "").toLowerCase();
let score = 0;
// Compliance stage (0-30)
if (stage.includes("procurement") || stage.includes("vendor review")) score += 30;
else if (stage.includes("due diligence") || stage.includes("evaluation")) score += 25;
else if (stage.includes("audit") || stage.includes("renewal")) score += 20;
else score += 10;
// Buying signal (0-25)
if (signal.includes("strong") || signal.includes("high")) score += 25;
else if (signal.includes("moderate") || signal.includes("medium")) score += 15;
else score += 5;
// Company size (0-20)
if (employees >= 1000) score += 20;
else if (employees >= 200) score += 15;
else if (employees >= 50) score += 10;
else score += 5;
// Existing relationship bonus (0-15)
if (lifecycleStage.includes("opportunity") || lifecycleStage.includes("customer")) score += 15;
else if (lifecycleStage.includes("lead") || lifecycleStage.includes("subscriber")) score += 8;
// Intent clarity (0-10)
if (intent.includes("specific") || intent.includes("deep")) score += 10;
else score += 3;
const finalScore = Math.min(100, score);
const priority = finalScore >= 70 ? "high" : finalScore >= 45 ? "medium" : "low";
return { security_interest_score: finalScore, engagement_priority: priority, needs_immediate_action: finalScore >= 65 ? "true" : "false" };`,
          outputVariables: [
            { name: "security_interest_score", type: "number" },
            { name: "engagement_priority", type: "string" },
            { name: "needs_immediate_action", type: "string" },
          ],
        },
      }),

      // 6. Branch by compliance stage condition
      n("6", "condition", "Branch by Compliance Stage Priority", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "engagement_priority",
              operator: "equals",
              value: "high",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Alert team member Slack
      slackNode(
        "7",
        "Alert Security Team via Slack",
        1480,
        ":shield: *Trust Center Visitor — {visitor_company}*\n*Industry:* {visitor_industry} | *Size:* {visitor_employee_count} employees\n*Compliance Stage:* {compliance_stage} | *Frameworks:* {likely_frameworks}\n*Interest Score:* {security_interest_score}/100 | *Priority:* {engagement_priority}\n*Intent:* {visitor_intent}\n*Concerns:* {security_concerns}\n*Recommended:* {recommended_engagement}\n*CRM Status:* {lifecycle_stage} | *Deal Stage Est:* {deal_stage_estimate}",
      ),

      // 8. Create security engagement task HubSpot
      hubspotPostNode(
        "8",
        "Create Security Engagement Task HubSpot",
        1720,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_body":"Trust Center Visit Follow-Up — {visitor_company}\\nCompliance stage: {compliance_stage}\\nFrameworks: {likely_frameworks}\\nConcerns: {security_concerns}\\nRecommended: {recommended_engagement}\\nInterest score: {security_interest_score}/100","hs_task_subject":"Security Engagement: {visitor_company} — {compliance_stage}","hs_task_status":"NOT_STARTED","hs_task_priority":"HIGH","hubspot_owner_id":"{owner_id}"}',
          },
        ],
        [{ name: "id", customName: "task_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1916. TYPEFORM LEAD CLASSIFICATION (8 nodes)
  // ===================================================================

  "Outgrow Forms Lead Classification": {
    summary:
      "Fires on a Outgrow Forms submission webhook to parse and normalize responses with runCode, classify lead intent and fit with AI, score lead priority with runCode, branch by lead classification, route hot leads to sales via HubSpot and Slack, enqueue warm leads for nurture email, and create a lead in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Outgrow Forms submission received", "webhook_app", {
        source: "outgrow-forms",
        event: "form_response.completed",
        howItStarts:
          "Fires when a Outgrow Forms submission is completed, parsing all answers, classifying the lead's intent and fit using AI, scoring priority, and routing to the appropriate channel — hot leads go to sales immediately while warm leads enter nurture sequences",
        setup:
          "Connect Outgrow Forms webhooks to fire on form completion. Set OpenAI API key for lead classification. Provide HubSpot token for CRM and deal creation. Configure Slack webhook for sales alerts. Configure SMTP for nurture emails.",
        inputFields: [],
      }),

      // 2. Parse normalize responses runCode
      n("2", "runCode", "Parse and Normalize Outgrow Forms Responses", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse Outgrow Forms webhook payload and normalize answers into structured data
const payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload;
const formResponse = payload.form_response || payload;
const answers = formResponse.answers || formResponse.fields || [];
// Extract key fields regardless of Outgrow Forms field naming
let email = "", name = "", company = "", role = "", teamSize = "", useCase = "", budget = "", timeline = "";
for (const ans of answers) {
  const fieldTitle = (ans.field?.title || ans.title || ans.question || "").toLowerCase();
  const value = ans.text || ans.email || ans.choice?.label || ans.number || ans.value || "";
  if (fieldTitle.includes("email") || ans.type === "email") email = value;
  else if (fieldTitle.includes("name") || fieldTitle.includes("full name")) name = value;
  else if (fieldTitle.includes("company") || fieldTitle.includes("organization")) company = value;
  else if (fieldTitle.includes("role") || fieldTitle.includes("title") || fieldTitle.includes("position")) role = value;
  else if (fieldTitle.includes("team") || fieldTitle.includes("size") || fieldTitle.includes("employees")) teamSize = value;
  else if (fieldTitle.includes("use case") || fieldTitle.includes("looking for") || fieldTitle.includes("need")) useCase = value;
  else if (fieldTitle.includes("budget") || fieldTitle.includes("spend")) budget = value;
  else if (fieldTitle.includes("timeline") || fieldTitle.includes("when") || fieldTitle.includes("urgency")) timeline = value;
}
const submittedAt = formResponse.submitted_at || formResponse.landed_at || new Date().toISOString();
return { lead_email: email, lead_name: name, lead_company: company, lead_role: role, team_size: teamSize, use_case: useCase, budget: budget, timeline: timeline, submitted_at: submittedAt, answer_count: answers.length, raw_answers: JSON.stringify(answers).substring(0, 500) };`,
          outputVariables: [
            { name: "lead_email", type: "string" },
            { name: "lead_name", type: "string" },
            { name: "lead_company", type: "string" },
            { name: "lead_role", type: "string" },
            { name: "team_size", type: "string" },
            { name: "use_case", type: "string" },
            { name: "budget", type: "string" },
            { name: "timeline", type: "string" },
            { name: "submitted_at", type: "string" },
            { name: "answer_count", type: "number" },
            { name: "raw_answers", type: "string" },
          ],
        },
      }),

      // 3. AI classify lead intent fit
      aiNode(
        "3",
        "AI Classify Lead Intent and Fit",
        520,
        "You are a lead qualification expert. Given the parsed form responses, classify the lead's intent (demo request, pricing inquiry, general interest, research, support) and fit (ideal customer profile match). Assess urgency and buying stage. Respond JSON: {intent_classification: string, icp_fit: string, buying_stage: string, urgency: string, qualification_notes: string, disqualification_flags: string, recommended_action: string}",
        "Lead: {lead_name} at {lead_company}. Role: {lead_role}. Team size: {team_size}. Use case: {use_case}. Budget: {budget}. Timeline: {timeline}. Answers: {raw_answers}. Classify this lead's intent and fit.",
        [
          {
            field: "intent_classification",
            as: "intent_classification",
            type: "string",
          },
          { field: "icp_fit", as: "icp_fit", type: "string" },
          { field: "buying_stage", as: "buying_stage", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          {
            field: "qualification_notes",
            as: "qualification_notes",
            type: "string",
          },
          {
            field: "disqualification_flags",
            as: "disqualification_flags",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),

      // 4. Score lead priority runCode
      n("4", "runCode", "Score Lead Priority", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score lead priority based on classification, fit, and urgency
const intent = (vars.intent_classification || "").toLowerCase();
const fit = (vars.icp_fit || "").toLowerCase();
const stage = (vars.buying_stage || "").toLowerCase();
const urgency = (vars.urgency || "").toLowerCase();
const disqualFlags = (vars.disqualification_flags || "none").toLowerCase();
let score = 0;
// Intent scoring (0-30)
if (intent.includes("demo")) score += 30;
else if (intent.includes("pricing")) score += 25;
else if (intent.includes("general interest")) score += 15;
else if (intent.includes("research")) score += 8;
else score += 5;
// ICP fit (0-25)
if (fit.includes("strong") || fit.includes("ideal") || fit.includes("excellent")) score += 25;
else if (fit.includes("good") || fit.includes("moderate")) score += 15;
else score += 5;
// Buying stage (0-20)
if (stage.includes("decision") || stage.includes("purchase")) score += 20;
else if (stage.includes("evaluation") || stage.includes("consideration")) score += 15;
else if (stage.includes("awareness") || stage.includes("research")) score += 8;
else score += 3;
// Urgency (0-15)
if (urgency.includes("immediate") || urgency.includes("high") || urgency.includes("urgent")) score += 15;
else if (urgency.includes("medium") || urgency.includes("this quarter")) score += 10;
else score += 3;
// Disqualification penalty
if (disqualFlags !== "none" && disqualFlags !== "") score -= 15;
const finalScore = Math.max(0, Math.min(100, score));
const classification = finalScore >= 70 ? "hot" : finalScore >= 40 ? "warm" : "cold";
return { lead_score: finalScore, lead_classification: classification, is_sales_ready: finalScore >= 65 ? "true" : "false" };`,
          outputVariables: [
            { name: "lead_score", type: "number" },
            { name: "lead_classification", type: "string" },
            { name: "is_sales_ready", type: "string" },
          ],
        },
      }),

      // 5. Branch by lead classification condition
      n("5", "condition", "Branch by Lead Classification", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "lead_classification",
              operator: "equals",
              value: "hot",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Route hot leads to sales HubSpot + Slack
      slackNode(
        "6",
        "Route Hot Lead to Sales Slack",
        1240,
        ":fire: *Hot Lead from Outgrow Forms — {lead_company}*\n*Contact:* {lead_name} ({lead_role})\n*Email:* {lead_email} | *Team Size:* {team_size}\n*Intent:* {intent_classification} | *Fit:* {icp_fit}\n*Score:* {lead_score}/100 | *Stage:* {buying_stage}\n*Use Case:* {use_case}\n*Budget:* {budget} | *Timeline:* {timeline}\n*Action:* {recommended_action}",
      ),

      // 7. Enqueue warm leads nurture email
      emailNode(
        "7",
        "Send Warm Lead Nurture Email",
        1480,
        "{lead_email}",
        "Thanks for your interest, {lead_name} — here is what is next",
        "<p>Hi {lead_name},</p><p>Thanks for sharing your interest in how we can help {lead_company}. Based on what you told us about your {use_case} needs, we have put together some resources that should be helpful.</p><p>Our team will follow up with personalized recommendations for your {team_size}-person team.</p><p>In the meantime, check out our resource center for guides tailored to {lead_role} professionals.</p>",
      ),

      // 8. Create lead CRM HubSpot
      hubspotPostNode(
        "8",
        "Create Lead in HubSpot CRM",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{lead_email}","firstname":"{lead_name}","company":"{lead_company}","jobtitle":"{lead_role}","hs_lead_status":"{lead_classification}","lead_score":"{lead_score}","intent_classification":"{intent_classification}","icp_fit":"{icp_fit}","use_case":"{use_case}","budget":"{budget}","timeline":"{timeline}"}',
          },
        ],
        [
          { name: "id", customName: "contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "created_email",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1917. UTM VISITOR QUALIFICATION PIPELINE (8 nodes)
  // ===================================================================

  "UTM Visitor Qualification Pipeline": {
    summary:
      "Fires on a UTM visit webhook to parse UTM parameters with runCode, determine campaign intent with runCode, enrich visitor profile via Clearbit API, score visitor quality with runCode, branch by intent and quality, route to appropriate channel in HubSpot, and report campaign qualification to Sheets.",
    nodes: [
      trigger("Webhook: UTM-tagged page visit", "webhook_app", {
        source: "website_tracking",
        event: "page.utm_visit",
        howItStarts:
          "Fires when a visitor arrives via a UTM-tagged link, parsing campaign parameters, determining the campaign intent, enriching the visitor profile, scoring quality, and routing qualified visitors to the appropriate sales or marketing channel",
        setup:
          "Connect your website tracking tool (Segment, Google Analytics events, or Clearbit Reveal) to fire webhooks on UTM-tagged visits. Set Clearbit API key for visitor enrichment. Provide HubSpot token for lead routing. Set Google Sheets credentials for campaign reporting.",
        inputFields: [],
      }),

      // 2. Parse UTM parameters runCode
      n("2", "runCode", "Parse UTM Parameters", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse UTM parameters from the webhook payload
const payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload;
const url = payload.url || payload.page_url || payload.referrer || "";
const params = payload.utm || payload.properties || payload;
const utmSource = params.utm_source || params.source || "";
const utmMedium = params.utm_medium || params.medium || "";
const utmCampaign = params.utm_campaign || params.campaign || "";
const utmContent = params.utm_content || params.content || "";
const utmTerm = params.utm_term || params.term || "";
const visitorDomain = payload.visitor_domain || payload.company_domain || payload.ip_domain || "";
const visitorIp = payload.ip || payload.visitor_ip || "";
const pageViewed = payload.page || payload.path || url.split("?")[0] || "";
const timestamp = payload.timestamp || payload.visited_at || new Date().toISOString();
return { utm_source: utmSource, utm_medium: utmMedium, utm_campaign: utmCampaign, utm_content: utmContent, utm_term: utmTerm, visitor_domain: visitorDomain, visitor_ip: visitorIp, page_viewed: pageViewed, visit_timestamp: timestamp };`,
          outputVariables: [
            { name: "utm_source", type: "string" },
            { name: "utm_medium", type: "string" },
            { name: "utm_campaign", type: "string" },
            { name: "utm_content", type: "string" },
            { name: "utm_term", type: "string" },
            { name: "visitor_domain", type: "string" },
            { name: "visitor_ip", type: "string" },
            { name: "page_viewed", type: "string" },
            { name: "visit_timestamp", type: "string" },
          ],
        },
      }),

      // 3. Determine campaign intent runCode
      n("3", "runCode", "Determine Campaign Intent", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine the campaign intent from UTM parameters and page context
const source = (vars.utm_source || "").toLowerCase();
const medium = (vars.utm_medium || "").toLowerCase();
const campaign = (vars.utm_campaign || "").toLowerCase();
const content = (vars.utm_content || "").toLowerCase();
const page = (vars.page_viewed || "").toLowerCase();
let intent = "awareness";
let intentScore = 30;
// Medium-based intent
if (medium.includes("cpc") || medium.includes("ppc") || medium.includes("paid")) { intent = "acquisition"; intentScore = 60; }
else if (medium.includes("email") || medium.includes("nurture")) { intent = "engagement"; intentScore = 50; }
else if (medium.includes("social")) { intent = "awareness"; intentScore = 30; }
else if (medium.includes("referral") || medium.includes("partner")) { intent = "referral"; intentScore = 55; }
// Campaign-based refinement
if (campaign.includes("demo") || campaign.includes("trial") || campaign.includes("free")) { intent = "conversion"; intentScore = 80; }
else if (campaign.includes("case-study") || campaign.includes("roi") || campaign.includes("comparison")) { intent = "evaluation"; intentScore = 70; }
else if (campaign.includes("webinar") || campaign.includes("ebook") || campaign.includes("guide")) { intent = "education"; intentScore = 45; }
else if (campaign.includes("retarget") || campaign.includes("remarket")) { intent = "re-engagement"; intentScore = 65; }
// Page-based boost
if (page.includes("pricing") || page.includes("demo") || page.includes("contact")) intentScore += 15;
else if (page.includes("product") || page.includes("features") || page.includes("integrations")) intentScore += 10;
return { campaign_intent: intent, intent_score: Math.min(100, intentScore), is_high_intent: intentScore >= 60 ? "true" : "false" };`,
          outputVariables: [
            { name: "campaign_intent", type: "string" },
            { name: "intent_score", type: "number" },
            { name: "is_high_intent", type: "string" },
          ],
        },
      }),

      // 4. Enrich visitor profile Clearbit API
      n("4", "hitApi", "Enrich Visitor Profile via Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://reveal.clearbit.com/v1/companies/find?domain={visitor_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "visitor_company", type: "string" },
            {
              name: "category.industry",
              customName: "visitor_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "visitor_employees",
              type: "number",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "visitor_revenue",
              type: "string",
            },
            {
              name: "category.sector",
              customName: "visitor_sector",
              type: "string",
            },
          ],
        },
      }),

      // 5. Score visitor quality runCode
      n("5", "runCode", "Score Visitor Quality", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score overall visitor quality combining intent and firmographic data
const intentScore = parseInt(vars.intent_score) || 0;
const employees = parseInt(vars.visitor_employees) || 0;
const revenue = (vars.visitor_revenue || "").toLowerCase();
const industry = (vars.visitor_industry || "").toLowerCase();
let qualityScore = 0;
// Intent contribution (0-40)
qualityScore += Math.round(intentScore * 0.4);
// Company size (0-25)
if (employees >= 500) qualityScore += 25;
else if (employees >= 100) qualityScore += 20;
else if (employees >= 20) qualityScore += 12;
else qualityScore += 5;
// Revenue tier (0-20)
if (revenue.includes("1b") || revenue.includes("billion")) qualityScore += 20;
else if (revenue.includes("100m") || revenue.includes("500m")) qualityScore += 18;
else if (revenue.includes("10m") || revenue.includes("50m")) qualityScore += 14;
else qualityScore += 5;
// Industry fit (0-15)
const targetIndustries = ["technology", "software", "saas", "internet", "financial"];
if (targetIndustries.some(t => industry.includes(t))) qualityScore += 15;
else qualityScore += 5;
const finalScore = Math.min(100, qualityScore);
const qualityTier = finalScore >= 70 ? "high" : finalScore >= 40 ? "medium" : "low";
return { visitor_quality_score: finalScore, quality_tier: qualityTier, is_qualified: finalScore >= 50 ? "true" : "false" };`,
          outputVariables: [
            { name: "visitor_quality_score", type: "number" },
            { name: "quality_tier", type: "string" },
            { name: "is_qualified", type: "string" },
          ],
        },
      }),

      // 6. Branch by intent + quality condition
      n("6", "condition", "Branch by Intent and Quality", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "quality_tier", operator: "equals", value: "high" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Route to appropriate channel HubSpot
      hubspotPostNode(
        "7",
        "Route Qualified Visitor to HubSpot",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"company":"{visitor_company}","industry":"{visitor_industry}","hs_lead_status":"NEW","utm_source":"{utm_source}","utm_medium":"{utm_medium}","utm_campaign":"{utm_campaign}","campaign_intent":"{campaign_intent}","visitor_quality_score":"{visitor_quality_score}","quality_tier":"{quality_tier}"}',
          },
        ],
        [{ name: "id", customName: "contact_id", type: "string" }],
      ),

      // 8. Report campaign qualification Sheets
      sheetsNode(
        "8",
        "Report Campaign Qualification to Sheets",
        1720,
        "UTM_Report",
        '[["=NOW()","{utm_source}","{utm_medium}","{utm_campaign}","{utm_content}","{visitor_company}","{visitor_industry}","{visitor_employees}","{campaign_intent}","{intent_score}","{visitor_quality_score}","{quality_tier}","{page_viewed}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1918. UNSUBSCRIBE SUPPRESSION SYNC (8 nodes)
  // ===================================================================

  "Unsubscribe Suppression Sync": {
    summary:
      "Fires on an unsubscribe event webhook to validate the unsubscribe request with runCode, branch by subscription type, suppress in email platform API, update CRM communication preferences in HubSpot, remove from active sequences API, suppress in ad audiences API, and log a compliance record to Sheets.",
    nodes: [
      trigger("Webhook: Unsubscribe event received", "webhook_app", {
        source: "email_platform",
        event: "contact.unsubscribed",
        howItStarts:
          "Fires when a contact unsubscribes from any communication channel, orchestrating suppression across all platforms — email, CRM, sequences, and ad audiences — to ensure full compliance and prevent re-contact",
        setup:
          "Connect your email platform (Postmark, Sendgrid, Mailchimp) to fire webhooks on unsubscribe events. Provide HubSpot token for CRM updates. Configure active sequence API. Configure ad platform APIs (Google Ads, Facebook). Set Google Sheets credentials for compliance logging.",
        inputFields: [],
      }),

      // 2. Validate unsubscribe request runCode
      n("2", "runCode", "Validate Unsubscribe Request", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse and validate the unsubscribe event
const payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload;
const email = payload.email || payload.recipient || payload.contact_email || payload.Recipient || "";
const subType = payload.subscription_type || payload.type || payload.list_name || payload.SuppressionGroup || "all";
const reason = payload.reason || payload.unsubscribe_reason || payload.Reason || "user_request";
const timestamp = payload.timestamp || payload.unsubscribed_at || payload.Timestamp || new Date().toISOString();
const listId = payload.list_id || payload.group_id || payload.ListID || "";
const messageId = payload.message_id || payload.MessageID || "";
const isValid = email && email.includes("@");
const subscriptionType = subType.toLowerCase().includes("marketing") ? "marketing" : subType.toLowerCase().includes("transactional") ? "transactional" : subType.toLowerCase().includes("product") ? "product_updates" : "all";
return { unsub_email: email, subscription_type: subscriptionType, unsub_reason: reason, unsub_timestamp: timestamp, list_id: listId, message_id: messageId, is_valid_unsub: isValid ? "true" : "false" };`,
          outputVariables: [
            { name: "unsub_email", type: "string" },
            { name: "subscription_type", type: "string" },
            { name: "unsub_reason", type: "string" },
            { name: "unsub_timestamp", type: "string" },
            { name: "list_id", type: "string" },
            { name: "message_id", type: "string" },
            { name: "is_valid_unsub", type: "string" },
          ],
        },
      }),

      // 3. Branch by subscription type condition
      n("3", "condition", "Branch by Subscription Type", 520, {
        conditionDetails: {
          conditions: [
            { variable: "subscription_type", operator: "equals", value: "all" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 4. Suppress in email platform API
      n("4", "hitApi", "Suppress in Email Platform", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/asm/suppressions/global",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "recipient_emails", value: '["{unsub_email}"]' }],
          variables: [
            {
              name: "recipient_emails",
              customName: "suppressed_emails",
              type: "string",
            },
          ],
        },
      }),

      // 5. Update CRM communication preferences HubSpot
      hubspotPostNode(
        "5",
        "Update CRM Communication Preferences",
        1000,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{unsub_email}","hs_email_optout":"true","unsubscribe_reason":"{unsub_reason}","unsubscribe_date":"{unsub_timestamp}","subscription_type_unsubscribed":"{subscription_type}"}',
          },
        ],
        [{ name: "id", customName: "contact_id", type: "string" }],
      ),

      // 6. Remove from active sequences API
      n("6", "hitApi", "Remove from Active Sequences", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/automation/v3/sequences/enrollments/unenroll",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "contactEmail", value: "{unsub_email}" }],
          variables: [
            { name: "status", customName: "unenroll_status", type: "string" },
          ],
        },
      }),

      // 7. Suppress in ad audiences API
      n("7", "hitApi", "Suppress in Ad Audiences", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/offlineUserDataJobs",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "job",
              value:
                '{"type":"CUSTOMER_MATCH_USER_LIST","customerMatchUserListMetadata":{"userList":"customers/{google_ads_customer_id}/userLists/{suppression_list_id}"}}',
            },
            {
              key: "operations",
              value:
                '[{"create":{"userIdentifiers":[{"hashedEmail":"{unsub_email}"}]}}]',
            },
          ],
          variables: [
            {
              name: "resourceName",
              customName: "ad_suppression_job",
              type: "string",
            },
          ],
        },
      }),

      // 8. Log compliance record Sheets
      sheetsNode(
        "8",
        "Log Compliance Record to Sheets",
        1720,
        "Unsubscribe_Log",
        '[["=NOW()","{unsub_email}","{subscription_type}","{unsub_reason}","{unsub_timestamp}","{list_id}","{message_id}","email_suppressed","crm_updated","sequences_removed","ads_suppressed"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1919. VIRAL POST COMMENT SCRAPER (9 nodes)
  // ===================================================================

  "Viral Post Comment Scraper": {
    summary:
      "Fires on a viral post alert webhook to scrape post comments via API, filter prospect signals with runCode, profile commenters with AI, enrich prospects via Clearbit API, score prospect quality with runCode, generate comment-aware outreach with AI, branch by prospect score, and queue outreach logging to email and HubSpot.",
    nodes: [
      trigger("Webhook: Viral post alert detected", "webhook_app", {
        source: "social_monitoring",
        event: "post.viral_threshold",
        howItStarts:
          "Fires when a social monitoring tool detects a viral post in your niche, scraping the comments for prospect signals, profiling engaged commenters, enriching their profiles, and generating personalized outreach that references their specific comment",
        setup:
          "Connect your social monitoring tool (Mention, Brand24, or Hootsuite) to fire webhooks on viral thresholds. Configure social platform API access for comment scraping. Set Clearbit API key for commenter enrichment. Set OpenAI API key for profiling and outreach. Provide HubSpot token for lead logging. Configure SMTP for outreach.",
        inputFields: [],
      }),

      // 2. Scrape post comments API
      n("2", "hitApi", "Scrape Viral Post Comments", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.linkedin.com/v2/socialActions/{post_urn}/comments?start=0&count=50",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "elements", customName: "comments_list", type: "string" },
            {
              name: "paging.total",
              customName: "total_comments",
              type: "number",
            },
          ],
        },
      }),

      // 3. Filter prospect signals runCode
      n("3", "runCode", "Filter Prospect Signals from Comments", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Filter comments for prospect signals — people expressing pain, interest, or decision-making authority
const payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload;
const postUrl = payload.post_url || payload.url || "";
const postAuthor = payload.post_author || payload.author || "";
const postTopic = payload.topic || payload.subject || "";
const comments = typeof vars.comments_list === "string" ? JSON.parse(vars.comments_list || "[]") : (vars.comments_list || []);
// Find the highest-signal commenter
let bestProspect = { name: "", comment: "", signal: "" };
let highestSignalScore = 0;
const signalKeywords = ["looking for", "we need", "struggling with", "anyone recommend", "we use", "switched to", "considering", "budget for", "evaluating", "decision"];
for (const c of comments.slice(0, 50)) {
  const text = (c.message?.text || c.comment || c.text || "").toLowerCase();
  const name = c.actor?.name || c.author?.name || c.name || "";
  let signalScore = 0;
  for (const kw of signalKeywords) { if (text.includes(kw)) signalScore += 10; }
  if (text.length > 100) signalScore += 5;
  if (signalScore > highestSignalScore) {
    highestSignalScore = signalScore;
    const matchedSignals = signalKeywords.filter(kw => text.includes(kw));
    bestProspect = { name: name, comment: c.message?.text || c.comment || c.text || "", signal: matchedSignals.join(", ") };
  }
}
return { prospect_name: bestProspect.name, prospect_comment: bestProspect.comment, prospect_signal: bestProspect.signal, signal_score: highestSignalScore, post_url: postUrl, post_author: postAuthor, post_topic: postTopic, comments_analyzed: Math.min(comments.length, 50) };`,
          outputVariables: [
            { name: "prospect_name", type: "string" },
            { name: "prospect_comment", type: "string" },
            { name: "prospect_signal", type: "string" },
            { name: "signal_score", type: "number" },
            { name: "post_url", type: "string" },
            { name: "post_author", type: "string" },
            { name: "post_topic", type: "string" },
            { name: "comments_analyzed", type: "number" },
          ],
        },
      }),

      // 4. AI profile commenters
      aiNode(
        "4",
        "AI Profile Prospect Commenter",
        760,
        "You are a social selling expert. Given a prospect's comment on a viral post, their name, and the post topic, build a prospect profile. Infer their role, seniority, likely company size, and pain points from the comment content. Identify what the comment reveals about their needs. Respond JSON: {inferred_role: string, inferred_seniority: string, pain_points: string, buying_indicators: string, engagement_approach: string, prospect_domain: string}",
        'Prospect name: {prospect_name}. Comment: \\"{prospect_comment}\\". Post topic: {post_topic}. Signal detected: {prospect_signal}. Post by: {post_author}. Profile this prospect based on their comment.',
        [
          { field: "inferred_role", as: "inferred_role", type: "string" },
          {
            field: "inferred_seniority",
            as: "inferred_seniority",
            type: "string",
          },
          { field: "pain_points", as: "pain_points", type: "string" },
          {
            field: "buying_indicators",
            as: "buying_indicators",
            type: "string",
          },
          {
            field: "engagement_approach",
            as: "engagement_approach",
            type: "string",
          },
          { field: "prospect_domain", as: "prospect_domain", type: "string" },
        ],
      ),

      // 5. Enrich prospect commenters Clearbit API
      n("5", "hitApi", "Enrich Prospect via Clearbit", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/combined/find?name={prospect_name}&domain={prospect_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "person.email",
              customName: "prospect_email",
              type: "string",
            },
            {
              name: "person.employment.title",
              customName: "prospect_title",
              type: "string",
            },
            {
              name: "person.employment.name",
              customName: "prospect_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_employees",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
          ],
        },
      }),

      // 6. Score prospect quality runCode
      n("6", "runCode", "Score Prospect Quality", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score prospect quality based on signal, profile, and enrichment data
const signalScore = parseInt(vars.signal_score) || 0;
const seniority = (vars.inferred_seniority || "").toLowerCase();
const title = (vars.prospect_title || "").toLowerCase();
const employees = parseInt(vars.company_employees) || 0;
const buyingIndicators = (vars.buying_indicators || "").toLowerCase();
let score = 0;
// Signal strength (0-30)
score += Math.min(30, signalScore);
// Seniority/title (0-25)
if (seniority.includes("c-level") || seniority.includes("executive") || title.includes("vp") || title.includes("chief") || title.includes("director")) score += 25;
else if (seniority.includes("senior") || title.includes("senior") || title.includes("head") || title.includes("manager")) score += 18;
else score += 8;
// Company size (0-20)
if (employees >= 200) score += 20;
else if (employees >= 50) score += 15;
else if (employees >= 10) score += 10;
else score += 5;
// Buying indicators (0-25)
if (buyingIndicators.includes("evaluating") || buyingIndicators.includes("budget") || buyingIndicators.includes("decision")) score += 25;
else if (buyingIndicators.includes("interest") || buyingIndicators.includes("looking")) score += 15;
else score += 5;
const finalScore = Math.min(100, score);
const tier = finalScore >= 70 ? "high" : finalScore >= 40 ? "medium" : "low";
return { prospect_score: finalScore, prospect_tier: tier, is_outreach_worthy: finalScore >= 45 ? "true" : "false" };`,
          outputVariables: [
            { name: "prospect_score", type: "number" },
            { name: "prospect_tier", type: "string" },
            { name: "is_outreach_worthy", type: "string" },
          ],
        },
      }),

      // 7. AI comment-aware outreach
      aiNode(
        "7",
        "AI Generate Comment-Aware Outreach",
        1480,
        "You are a social selling expert who turns viral post engagement into warm outreach. Write a personalized email that naturally references the prospect's specific comment on the viral post. Don't be creepy — frame it as shared interest in the topic. Connect their comment to a relevant solution. Under 120 words. Respond JSON: {subject: string, body: string, comment_reference: string}",
        'Prospect: {prospect_name}, {prospect_title} at {prospect_company}. Their comment: \\"{prospect_comment}\\". Pain points: {pain_points}. Post topic: {post_topic}. Post by: {post_author}. Engagement approach: {engagement_approach}. Prospect score: {prospect_score}/100. Write comment-aware outreach.',
        [
          { field: "subject", as: "outreach_subject", type: "string" },
          { field: "body", as: "outreach_body", type: "string" },
          {
            field: "comment_reference",
            as: "comment_reference",
            type: "string",
          },
        ],
      ),

      // 8. Branch by prospect score condition
      n("8", "condition", "Branch by Prospect Score", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "prospect_tier", operator: "equals", value: "high" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 9. Queue outreach log email + HubSpot
      hubspotPostNode(
        "9",
        "Queue Outreach and Log to HubSpot",
        1960,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{prospect_email}","firstname":"{prospect_name}","company":"{prospect_company}","jobtitle":"{prospect_title}","hs_lead_status":"NEW","prospect_score":"{prospect_score}","source_post_url":"{post_url}","prospect_comment":"{prospect_comment}","outreach_subject":"{outreach_subject}"}',
          },
        ],
        [{ name: "id", customName: "contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("8", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1920. VOICE-CALL TASK CREATOR (8 nodes)
  // ===================================================================

  "Voice-Call Task Creator": {
    summary:
      "Fires on a call task request webhook to validate phone numbers via Twilio API, filter valid numbers with runCode, fetch contact context from HubSpot, generate AI talk tracks, assign optimal call windows with runCode, create call tasks in HubSpot, and notify reps via Slack.",
    nodes: [
      trigger("Webhook: Call task request submitted", "webhook_app", {
        source: "sales_ops",
        event: "call_task.batch_request",
        howItStarts:
          "Fires when a batch call task request is submitted, validating phone numbers via Twilio, fetching contact context, generating personalized talk tracks with AI, assigning optimal call windows based on timezone and availability, and creating ready-to-execute call tasks in HubSpot",
        setup:
          "Set Twilio API credentials for phone number validation. Provide HubSpot token for contact context and task creation. Set OpenAI API key for talk track generation. Configure Slack webhook for rep notifications.",
        inputFields: [],
      }),

      // 2. Validate phone numbers Twilio API
      n("2", "hitApi", "Validate Phone Numbers via Twilio", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://lookups.twilio.com/v2/PhoneNumbers/{phone_number}?Fields=line_type_intelligence",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "valid", customName: "phone_valid", type: "boolean" },
            {
              name: "phone_number",
              customName: "validated_phone",
              type: "string",
            },
            {
              name: "country_code",
              customName: "phone_country",
              type: "string",
            },
            {
              name: "line_type_intelligence.type",
              customName: "line_type",
              type: "string",
            },
            {
              name: "caller_name.caller_name",
              customName: "caller_name",
              type: "string",
            },
          ],
        },
      }),

      // 3. Filter valid numbers runCode
      n("3", "runCode", "Filter Valid Phone Numbers", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook payload and filter valid phone numbers
const payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload;
const contacts = payload.contacts || payload.leads || [payload];
const contactId = contacts[0]?.contact_id || contacts[0]?.id || payload.contact_id || "";
const contactName = contacts[0]?.name || payload.name || vars.caller_name || "";
const contactEmail = contacts[0]?.email || payload.email || "";
const phone = vars.validated_phone || contacts[0]?.phone || payload.phone || "";
const isValid = vars.phone_valid === true || vars.phone_valid === "true";
const lineType = (vars.line_type || "").toLowerCase();
const isMobile = lineType.includes("mobile") || lineType.includes("cell");
const country = vars.phone_country || "US";
const campaignName = payload.campaign || payload.task_name || "Outbound Call Campaign";
const priority = payload.priority || "normal";
return { contact_id: contactId, contact_name: contactName, contact_email: contactEmail, phone_number: phone, is_valid_phone: isValid ? "true" : "false", is_mobile: isMobile ? "true" : "false", phone_country: country, campaign_name: campaignName, call_priority: priority, line_type: lineType };`,
          outputVariables: [
            { name: "contact_id", type: "string" },
            { name: "contact_name", type: "string" },
            { name: "contact_email", type: "string" },
            { name: "phone_number", type: "string" },
            { name: "is_valid_phone", type: "string" },
            { name: "is_mobile", type: "string" },
            { name: "phone_country", type: "string" },
            { name: "campaign_name", type: "string" },
            { name: "call_priority", type: "string" },
            { name: "line_type", type: "string" },
          ],
        },
      }),

      // 4. Fetch contact context HubSpot
      hubspotGetNode(
        "4",
        "Fetch Contact Context HubSpot",
        760,
        "crm/v3/objects/contacts/{contact_id}?properties=firstname,lastname,company,jobtitle,lifecyclestage,hs_lead_status,notes_last_updated",
        [
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "company_name",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "job_title",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),

      // 5. AI generate talk tracks
      aiNode(
        "5",
        "AI Generate Personalized Talk Tracks",
        1000,
        "You are a sales enablement coach. Given the contact's profile, company, title, lifecycle stage, and call campaign context, generate a concise talk track. Include an opener, 2-3 discovery questions, a value proposition, and an objection handler. Keep practical and conversational. Respond JSON: {opener: string, discovery_questions: string, value_prop: string, objection_handler: string, call_objective: string, estimated_call_duration: string}",
        "Contact: {first_name} {last_name}, {job_title} at {company_name}. Status: {lead_status}. Lifecycle: {lifecycle_stage}. Campaign: {campaign_name}. Priority: {call_priority}. Phone type: {line_type}. Generate the call talk track.",
        [
          { field: "opener", as: "talk_track_opener", type: "string" },
          {
            field: "discovery_questions",
            as: "discovery_questions",
            type: "string",
          },
          { field: "value_prop", as: "value_proposition", type: "string" },
          {
            field: "objection_handler",
            as: "objection_handler",
            type: "string",
          },
          { field: "call_objective", as: "call_objective", type: "string" },
          {
            field: "estimated_call_duration",
            as: "estimated_duration",
            type: "string",
          },
        ],
      ),

      // 6. Assign optimal call windows runCode
      n("6", "runCode", "Assign Optimal Call Windows", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine optimal call windows based on timezone, role, and priority
const country = (vars.phone_country || "US").toUpperCase();
const title = (vars.job_title || "").toLowerCase();
const priority = (vars.call_priority || "normal").toLowerCase();
const isMobile = vars.is_mobile === "true";
// Timezone-based optimal windows
let timezone = "America/New_York";
let window1 = "9:00 AM - 10:30 AM";
let window2 = "2:00 PM - 3:30 PM";
if (country === "GB" || country === "UK") { timezone = "Europe/London"; window1 = "9:30 AM - 11:00 AM"; window2 = "1:30 PM - 3:00 PM"; }
else if (country === "DE" || country === "FR") { timezone = "Europe/Berlin"; window1 = "10:00 AM - 11:30 AM"; window2 = "2:00 PM - 3:30 PM"; }
// Role-based adjustments
if (title.includes("ceo") || title.includes("chief") || title.includes("founder")) { window1 = "8:00 AM - 9:00 AM"; window2 = "4:00 PM - 5:00 PM"; }
else if (title.includes("vp") || title.includes("director")) { window1 = "8:30 AM - 10:00 AM"; window2 = "3:00 PM - 4:30 PM"; }
// Priority adjustment
const callDate = priority === "high" ? "today" : priority === "urgent" ? "today_asap" : "next_business_day";
const channel = isMobile ? "mobile_preferred" : "office_line";
return { call_timezone: timezone, call_window_1: window1, call_window_2: window2, call_date: callDate, call_channel: channel };`,
          outputVariables: [
            { name: "call_timezone", type: "string" },
            { name: "call_window_1", type: "string" },
            { name: "call_window_2", type: "string" },
            { name: "call_date", type: "string" },
            { name: "call_channel", type: "string" },
          ],
        },
      }),

      // 7. Create call tasks HubSpot
      hubspotPostNode(
        "7",
        "Create Call Task in HubSpot",
        1480,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_body":"Call Task — {campaign_name}\\nContact: {first_name} {last_name} ({job_title}) at {company_name}\\nPhone: {phone_number} ({line_type})\\nTimezone: {call_timezone}\\nWindows: {call_window_1} or {call_window_2}\\nChannel: {call_channel}\\n\\nTalk Track:\\nOpener: {talk_track_opener}\\nDiscovery: {discovery_questions}\\nValue Prop: {value_proposition}\\nObjection: {objection_handler}\\nObjective: {call_objective}\\nEst. Duration: {estimated_duration}","hs_task_subject":"Call: {first_name} {last_name} — {campaign_name}","hs_task_status":"NOT_STARTED","hs_task_priority":"{call_priority}","hs_task_type":"CALL"}',
          },
        ],
        [{ name: "id", customName: "task_id", type: "string" }],
      ),

      // 8. Notify reps Slack
      slackNode(
        "8",
        "Notify Reps of Call Tasks",
        1720,
        ":telephone_receiver: *New Call Task — {campaign_name}*\n*Contact:* {first_name} {last_name}, {job_title} at {company_name}\n*Phone:* {phone_number} ({line_type}) | *Priority:* {call_priority}\n*Windows:* {call_window_1} or {call_window_2} ({call_timezone})\n*Objective:* {call_objective}\n*Opener:* {talk_track_opener}\n*HubSpot Task:* Created (ID: {task_id})",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1921. WARM-INTRO MAPPING (8 nodes)
  // ===================================================================

  "Warm-Intro Mapping": {
    summary:
      "Fires on an intro request webhook to fetch target contact details via Clearbit API, search CRM mutual connections in HubSpot, search LinkedIn connection paths with runCode, rank introduction paths with runCode, generate AI intro request emails, branch by path quality, and create intro tasks in HubSpot.",
    nodes: [
      trigger("Webhook: Warm intro request", "webhook_app", {
        source: "sales_ops",
        event: "intro.request_submitted",
        howItStarts:
          "Fires when a sales rep requests a warm introduction to a target contact, mapping all available introduction paths through CRM connections, LinkedIn networks, and mutual contacts, then generating personalized intro request emails for the strongest paths",
        setup:
          "Set Clearbit API key for target contact enrichment. Provide HubSpot token for CRM connection search and task creation. Set OpenAI API key for intro email generation. Configure LinkedIn data API access for connection path analysis.",
        inputFields: [],
      }),

      // 2. Fetch target contact details Clearbit API
      n("2", "hitApi", "Fetch Target Contact via Clearbit", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/combined/find?email={target_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "person.fullName",
              customName: "target_name",
              type: "string",
            },
            {
              name: "person.employment.title",
              customName: "target_title",
              type: "string",
            },
            {
              name: "person.employment.name",
              customName: "target_company",
              type: "string",
            },
            {
              name: "person.employment.domain",
              customName: "target_domain",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "target_company_size",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "target_industry",
              type: "string",
            },
            {
              name: "person.linkedin.handle",
              customName: "target_linkedin",
              type: "string",
            },
          ],
        },
      }),

      // 3. Search CRM mutual connections HubSpot
      hubspotGetNode(
        "3",
        "Search CRM for Mutual Connections",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results[0].properties.email",
            customName: "mutual_contact_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "mutual_contact_first",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "mutual_contact_last",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "mutual_contact_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "mutual_contact_title",
            type: "string",
          },
          {
            name: "total",
            customName: "mutual_contacts_found",
            type: "number",
          },
        ],
      ),

      // 4. Search LinkedIn connection paths runCode
      n("4", "runCode", "Search LinkedIn Connection Paths", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze available introduction paths (CRM, LinkedIn, personal network)
const targetName = vars.target_name || "";
const targetCompany = vars.target_company || "";
const targetTitle = vars.target_title || "";
const mutualEmail = vars.mutual_contact_email || "";
const mutualFirst = vars.mutual_contact_first || "";
const mutualLast = vars.mutual_contact_last || "";
const mutualCompany = vars.mutual_contact_company || "";
const mutualTitle = vars.mutual_contact_title || "";
const mutualCount = parseInt(vars.mutual_contacts_found) || 0;
const paths = [];
// CRM path
if (mutualEmail) {
  const isCustomer = mutualCompany.toLowerCase().includes(targetCompany.toLowerCase().split(" ")[0]);
  paths.push({
    type: "crm_mutual_connection",
    connector: mutualFirst + " " + mutualLast,
    connector_email: mutualEmail,
    connector_company: mutualCompany,
    connector_title: mutualTitle,
    strength: isCustomer ? "strong" : "moderate",
    relationship: isCustomer ? "same_company" : "industry_peer"
  });
}
// LinkedIn path placeholder
paths.push({ type: "linkedin_2nd_degree", connector: "LinkedIn network", connector_email: "", connector_company: "", connector_title: "", strength: "moderate", relationship: "2nd_degree_connection" });
const bestPath = paths.sort((a, b) => (b.strength === "strong" ? 1 : 0) - (a.strength === "strong" ? 1 : 0))[0];
return { best_path_type: bestPath.type, connector_name: bestPath.connector, connector_email: bestPath.connector_email, connector_company: bestPath.connector_company, connector_title: bestPath.connector_title, path_strength: bestPath.strength, relationship_type: bestPath.relationship, total_paths_found: paths.length };`,
          outputVariables: [
            { name: "best_path_type", type: "string" },
            { name: "connector_name", type: "string" },
            { name: "connector_email", type: "string" },
            { name: "connector_company", type: "string" },
            { name: "connector_title", type: "string" },
            { name: "path_strength", type: "string" },
            { name: "relationship_type", type: "string" },
            { name: "total_paths_found", type: "number" },
          ],
        },
      }),

      // 5. Rank introduction paths runCode
      n("5", "runCode", "Rank Introduction Paths", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score and rank the introduction path quality
const pathStrength = (vars.path_strength || "").toLowerCase();
const pathType = (vars.best_path_type || "").toLowerCase();
const relationship = (vars.relationship_type || "").toLowerCase();
const connectorTitle = (vars.connector_title || "").toLowerCase();
const totalPaths = parseInt(vars.total_paths_found) || 0;
let score = 0;
// Path strength (0-35)
if (pathStrength === "strong") score += 35;
else if (pathStrength === "moderate") score += 20;
else score += 8;
// Path type (0-25)
if (pathType.includes("crm")) score += 25;
else if (pathType.includes("linkedin_1st")) score += 20;
else if (pathType.includes("linkedin_2nd")) score += 12;
else score += 5;
// Relationship (0-20)
if (relationship.includes("same_company") || relationship.includes("colleague")) score += 20;
else if (relationship.includes("industry_peer") || relationship.includes("advisor")) score += 15;
else score += 8;
// Connector seniority (0-20)
if (connectorTitle.includes("ceo") || connectorTitle.includes("vp") || connectorTitle.includes("chief") || connectorTitle.includes("director")) score += 20;
else if (connectorTitle.includes("senior") || connectorTitle.includes("head") || connectorTitle.includes("manager")) score += 12;
else score += 5;
const finalScore = Math.min(100, score);
const quality = finalScore >= 70 ? "high" : finalScore >= 40 ? "medium" : "low";
return { path_quality_score: finalScore, path_quality: quality, is_viable_intro: finalScore >= 35 ? "true" : "false" };`,
          outputVariables: [
            { name: "path_quality_score", type: "number" },
            { name: "path_quality", type: "string" },
            { name: "is_viable_intro", type: "string" },
          ],
        },
      }),

      // 6. AI intro request emails
      aiNode(
        "6",
        "AI Generate Intro Request Email",
        1240,
        "You are a professional relationship manager. Write two emails: (1) a request to the connector asking them to make the introduction, and (2) a suggested forwardable blurb they can send to the target. The connector email should be warm, explain why the intro matters, and make it easy for them. The forwardable should be brief and compelling. Respond JSON: {connector_subject: string, connector_body: string, forwardable_blurb: string, intro_reason: string}",
        "Target: {target_name}, {target_title} at {target_company} ({target_industry}). Connector: {connector_name} ({connector_title} at {connector_company}). Relationship: {relationship_type}. Path quality: {path_quality} ({path_quality_score}/100). Write the intro request emails.",
        [
          {
            field: "connector_subject",
            as: "connector_subject",
            type: "string",
          },
          { field: "connector_body", as: "connector_body", type: "string" },
          {
            field: "forwardable_blurb",
            as: "forwardable_blurb",
            type: "string",
          },
          { field: "intro_reason", as: "intro_reason", type: "string" },
        ],
      ),

      // 7. Branch by path quality condition
      n("7", "condition", "Branch by Intro Path Quality", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "path_quality", operator: "equals", value: "high" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Create intro tasks HubSpot
      hubspotPostNode(
        "8",
        "Create Intro Task in HubSpot",
        1720,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_body":"Warm Intro Request\\nTarget: {target_name}, {target_title} at {target_company}\\nConnector: {connector_name} ({connector_title} at {connector_company})\\nPath: {best_path_type} | Quality: {path_quality} ({path_quality_score}/100)\\nRelationship: {relationship_type}\\n\\nConnector Email Subject: {connector_subject}\\nForwardable Blurb: {forwardable_blurb}\\nReason: {intro_reason}","hs_task_subject":"Warm Intro: {connector_name} -> {target_name} at {target_company}","hs_task_status":"NOT_STARTED","hs_task_priority":"MEDIUM"}',
          },
        ],
        [{ name: "id", customName: "task_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1922. WEBHOOK RETRY WITH BACKOFF (7 nodes)
  // ===================================================================

  "Webhook Retry with Backoff": {
    summary:
      "Fires on a failed delivery webhook to log failure details with runCode, calculate backoff delay with runCode, branch by retry count, retry the webhook delivery via API, handle persistent failures with runCode, and alert the operations team via Slack.",
    nodes: [
      trigger("Webhook: Delivery failure notification", "webhook_app", {
        source: "webhook_monitor",
        event: "delivery.failed",
        howItStarts:
          "Fires when a webhook delivery fails, implementing intelligent retry logic with exponential backoff — calculating appropriate delays, retrying the delivery, tracking attempts, and escalating persistent failures to the operations team",
        setup:
          "Connect your webhook delivery service or monitoring tool to fire notifications on delivery failures. Set OpenAI API key (not needed for this workflow). Configure Slack webhook for operations team alerts.",
        inputFields: [],
      }),

      // 2. Log failure details runCode
      n("2", "runCode", "Log Failure Details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse the failed delivery event and extract retry metadata
const payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload;
const webhookId = payload.webhook_id || payload.id || payload.delivery_id || "";
const targetUrl = payload.target_url || payload.url || payload.endpoint || "";
const httpStatus = payload.status_code || payload.response_code || payload.http_status || 0;
const errorMessage = payload.error || payload.error_message || payload.failure_reason || "Unknown error";
const requestBody = payload.request_body || payload.payload || "{}";
const retryCount = parseInt(payload.retry_count || payload.attempt || "0") || 0;
const firstFailedAt = payload.first_failed_at || payload.created_at || new Date().toISOString();
const maxRetries = 5;
const isRetryable = httpStatus >= 500 || httpStatus === 429 || httpStatus === 408 || httpStatus === 0;
return { webhook_id: webhookId, target_url: targetUrl, http_status: httpStatus, error_message: errorMessage, request_body: requestBody, retry_count: retryCount, first_failed_at: firstFailedAt, max_retries: maxRetries, is_retryable: isRetryable ? "true" : "false" };`,
          outputVariables: [
            { name: "webhook_id", type: "string" },
            { name: "target_url", type: "string" },
            { name: "http_status", type: "number" },
            { name: "error_message", type: "string" },
            { name: "request_body", type: "string" },
            { name: "retry_count", type: "number" },
            { name: "first_failed_at", type: "string" },
            { name: "max_retries", type: "number" },
            { name: "is_retryable", type: "string" },
          ],
        },
      }),

      // 3. Calculate backoff delay runCode
      n("3", "runCode", "Calculate Backoff Delay", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate exponential backoff delay with jitter
const retryCount = parseInt(vars.retry_count) || 0;
const maxRetries = parseInt(vars.max_retries) || 5;
const isRetryable = vars.is_retryable === "true";
const httpStatus = parseInt(vars.http_status) || 0;
// Exponential backoff: base * 2^retry with jitter
const baseDelay = 30; // 30 seconds
const maxDelay = 3600; // 1 hour max
let delay = 0;
if (isRetryable && retryCount < maxRetries) {
  delay = Math.min(maxDelay, baseDelay * Math.pow(2, retryCount));
  // Add 10% jitter
  const jitter = Math.round(delay * 0.1 * Math.random());
  delay += jitter;
}
const shouldRetry = isRetryable && retryCount < maxRetries;
const nextRetryNumber = retryCount + 1;
const delayMinutes = Math.round(delay / 60);
const retryStatus = shouldRetry ? "will_retry" : (retryCount >= maxRetries ? "max_retries_exceeded" : "non_retryable");
// Rate limit special handling
if (httpStatus === 429) { delay = Math.max(delay, 120); }
return { backoff_delay_seconds: delay, backoff_delay_minutes: delayMinutes, should_retry: shouldRetry ? "true" : "false", next_retry_number: nextRetryNumber, retry_status: retryStatus };`,
          outputVariables: [
            { name: "backoff_delay_seconds", type: "number" },
            { name: "backoff_delay_minutes", type: "number" },
            { name: "should_retry", type: "string" },
            { name: "next_retry_number", type: "number" },
            { name: "retry_status", type: "string" },
          ],
        },
      }),

      // 4. Branch by retry count condition
      n("4", "condition", "Branch by Retry Eligibility", 760, {
        conditionDetails: {
          conditions: [
            { variable: "should_retry", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Retry webhook delivery API
      n("5", "hitApi", "Retry Webhook Delivery", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "{target_url}",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Retry-Count", value: "{next_retry_number}" },
            { key: "X-Original-Webhook-Id", value: "{webhook_id}" },
          ],
          body: [{ key: "payload", value: "{request_body}" }],
          variables: [
            {
              name: "status",
              customName: "retry_response_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. Handle persistent failure runCode
      n("6", "runCode", "Handle Persistent Failure", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Handle persistent webhook delivery failure
const webhookId = vars.webhook_id || "";
const targetUrl = vars.target_url || "";
const httpStatus = vars.http_status || "unknown";
const errorMessage = vars.error_message || "Unknown";
const retryCount = parseInt(vars.retry_count) || 0;
const firstFailedAt = vars.first_failed_at || "";
const retryStatus = vars.retry_status || "";
const now = new Date().toISOString();
// Calculate total failure duration
const failStart = new Date(firstFailedAt || now);
const failDuration = Math.round((Date.now() - failStart.getTime()) / 60000);
const severity = retryStatus === "max_retries_exceeded" ? "critical" : retryStatus === "non_retryable" ? "high" : "medium";
const recommendation = retryStatus === "max_retries_exceeded"
  ? "All retry attempts exhausted. Manual investigation required. Check endpoint health and contact the integration owner."
  : retryStatus === "non_retryable"
  ? "Error is non-retryable (HTTP " + httpStatus + "). Fix the request payload or endpoint configuration."
  : "Delivery scheduled for retry with backoff.";
return { failure_severity: severity, failure_duration_minutes: failDuration, failure_recommendation: recommendation, failure_timestamp: now, failure_summary: "Webhook " + webhookId + " to " + targetUrl + " failed " + (retryCount + 1) + " times. Status: " + httpStatus + ". Duration: " + failDuration + "min." };`,
          outputVariables: [
            { name: "failure_severity", type: "string" },
            { name: "failure_duration_minutes", type: "number" },
            { name: "failure_recommendation", type: "string" },
            { name: "failure_timestamp", type: "string" },
            { name: "failure_summary", type: "string" },
          ],
        },
      }),

      // 7. Alert operations team Slack
      slackNode(
        "7",
        "Alert Operations Team Slack",
        1480,
        ":warning: *Webhook Delivery Failure — {failure_severity}*\n*Webhook ID:* {webhook_id}\n*Target:* {target_url}\n*HTTP Status:* {http_status} | *Error:* {error_message}\n*Retries:* {retry_count}/{max_retries} | *Status:* {retry_status}\n*Duration:* {failure_duration_minutes} minutes\n*Summary:* {failure_summary}\n*Recommendation:* {failure_recommendation}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1923. WEBINAR ATTENDEE AE BRIEF (9 nodes)
  // ===================================================================

  "Webinar Attendee AE Brief": {
    summary:
      "Fires on a webinar completed webhook to fetch attendee engagement metrics with runCode, enrich attendee profiles via Clearbit API, score attendee sales readiness with runCode, generate personalized AE briefs with AI, branch by engagement tier, create AE follow-up tasks in HubSpot, send automated mid-tier follow-up email, and alert AEs via Slack.",
    nodes: [
      trigger("Webhook: Webinar session completed", "webhook_app", {
        source: "webinar_platform",
        event: "webinar.session_completed",
        howItStarts:
          "Fires when a webinar session ends, processing attendee engagement data, enriching profiles, scoring sales readiness, generating personalized AE briefs, and routing follow-ups by engagement tier — top attendees get AE outreach while mid-tier gets automated nurture",
        setup:
          "Connect your webinar platform (Zoom, GoToWebinar, or Demio) to fire webhooks on session completion. Set Clearbit API key for attendee enrichment. Set OpenAI API key for brief generation. Provide HubSpot token for task creation. Configure Slack webhook and SMTP for notifications.",
        inputFields: [],
      }),

      // 2. Fetch attendee engagement metrics runCode
      n("2", "runCode", "Fetch Attendee Engagement Metrics", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webinar completion data and extract attendee engagement metrics
const payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload;
const webinarTitle = payload.webinar_title || payload.topic || payload.name || "";
const webinarDate = payload.date || payload.start_time || new Date().toISOString();
const attendees = payload.attendees || payload.participants || [payload];
// Get the most engaged attendee for processing
const topAttendee = attendees.sort((a, b) => (b.attendance_duration || b.duration || 0) - (a.attendance_duration || a.duration || 0))[0] || {};
const attendeeEmail = topAttendee.email || topAttendee.user_email || "";
const attendeeName = topAttendee.name || topAttendee.display_name || topAttendee.first_name || "";
const duration = topAttendee.attendance_duration || topAttendee.duration || 0;
const totalDuration = payload.total_duration || payload.webinar_duration || 60;
const attendanceRate = totalDuration > 0 ? Math.round((duration / totalDuration) * 100) : 0;
const questionsAsked = topAttendee.questions_asked || topAttendee.questions || 0;
const pollsAnswered = topAttendee.polls_answered || topAttendee.poll_responses || 0;
const chatMessages = topAttendee.chat_messages || topAttendee.chats || 0;
const resourcesDownloaded = topAttendee.resources_downloaded || topAttendee.downloads || 0;
return { webinar_title: webinarTitle, webinar_date: webinarDate, attendee_email: attendeeEmail, attendee_name: attendeeName, attendance_duration_min: duration, total_webinar_duration: totalDuration, attendance_rate: attendanceRate, questions_asked: questionsAsked, polls_answered: pollsAnswered, chat_messages: chatMessages, resources_downloaded: resourcesDownloaded, total_attendees: attendees.length };`,
          outputVariables: [
            { name: "webinar_title", type: "string" },
            { name: "webinar_date", type: "string" },
            { name: "attendee_email", type: "string" },
            { name: "attendee_name", type: "string" },
            { name: "attendance_duration_min", type: "number" },
            { name: "total_webinar_duration", type: "number" },
            { name: "attendance_rate", type: "number" },
            { name: "questions_asked", type: "number" },
            { name: "polls_answered", type: "number" },
            { name: "chat_messages", type: "number" },
            { name: "resources_downloaded", type: "number" },
            { name: "total_attendees", type: "number" },
          ],
        },
      }),

      // 3. Enrich attendee profiles Clearbit API
      n("3", "hitApi", "Enrich Attendee Profile via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/combined/find?email={attendee_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "person.employment.title",
              customName: "attendee_title",
              type: "string",
            },
            {
              name: "person.employment.name",
              customName: "attendee_company",
              type: "string",
            },
            {
              name: "person.employment.seniority",
              customName: "attendee_seniority",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_employees",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "company.metrics.estimatedAnnualRevenue",
              customName: "company_revenue",
              type: "string",
            },
          ],
        },
      }),

      // 4. Score attendee sales readiness runCode
      n("4", "runCode", "Score Attendee Sales Readiness", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score attendee sales readiness from engagement and firmographic data
const attendanceRate = parseInt(vars.attendance_rate) || 0;
const questions = parseInt(vars.questions_asked) || 0;
const polls = parseInt(vars.polls_answered) || 0;
const chats = parseInt(vars.chat_messages) || 0;
const downloads = parseInt(vars.resources_downloaded) || 0;
const seniority = (vars.attendee_seniority || "").toLowerCase();
const employees = parseInt(vars.company_employees) || 0;
let score = 0;
// Engagement scoring (0-45)
score += Math.min(20, Math.round(attendanceRate * 0.2));
score += Math.min(10, questions * 5);
score += Math.min(5, polls * 2);
score += Math.min(5, chats * 2);
score += Math.min(5, downloads * 3);
// Seniority (0-25)
if (seniority.includes("executive") || seniority.includes("c_level")) score += 25;
else if (seniority.includes("director") || seniority.includes("vp")) score += 20;
else if (seniority.includes("manager") || seniority.includes("senior")) score += 12;
else score += 5;
// Company size (0-20)
if (employees >= 500) score += 20;
else if (employees >= 100) score += 15;
else if (employees >= 20) score += 10;
else score += 5;
// Engagement bonus (0-10)
const totalInteractions = questions + polls + chats + downloads;
if (totalInteractions >= 5) score += 10;
else if (totalInteractions >= 2) score += 5;
const finalScore = Math.min(100, score);
const tier = finalScore >= 70 ? "hot" : finalScore >= 40 ? "warm" : "cold";
return { readiness_score: finalScore, engagement_tier: tier, total_interactions: totalInteractions, is_sales_qualified: finalScore >= 60 ? "true" : "false" };`,
          outputVariables: [
            { name: "readiness_score", type: "number" },
            { name: "engagement_tier", type: "string" },
            { name: "total_interactions", type: "number" },
            { name: "is_sales_qualified", type: "string" },
          ],
        },
      }),

      // 5. AI personalized AE briefs
      aiNode(
        "5",
        "AI Generate Personalized AE Brief",
        1000,
        "You are a sales intelligence analyst preparing an AE brief after a webinar. Given the attendee's engagement data, profile, and company info, write a concise brief that helps the AE have a productive follow-up conversation. Include what the attendee cared about (based on engagement signals), talking points, and a suggested opening. Respond JSON: {brief_summary: string, interest_signals: string, talking_points: string, suggested_opener: string, follow_up_urgency: string, recommended_next_step: string}",
        "Attendee: {attendee_name}, {attendee_title} at {attendee_company} ({company_industry}, {company_employees} employees). Webinar: {webinar_title}. Attendance: {attendance_rate}%. Questions: {questions_asked}. Polls: {polls_answered}. Chat messages: {chat_messages}. Downloads: {resources_downloaded}. Readiness: {readiness_score}/100 ({engagement_tier}). Seniority: {attendee_seniority}. Generate the AE brief.",
        [
          { field: "brief_summary", as: "brief_summary", type: "string" },
          { field: "interest_signals", as: "interest_signals", type: "string" },
          { field: "talking_points", as: "talking_points", type: "string" },
          { field: "suggested_opener", as: "suggested_opener", type: "string" },
          {
            field: "follow_up_urgency",
            as: "follow_up_urgency",
            type: "string",
          },
          {
            field: "recommended_next_step",
            as: "recommended_next_step",
            type: "string",
          },
        ],
      ),

      // 6. Branch by engagement tier condition
      n("6", "condition", "Branch by Engagement Tier", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "engagement_tier", operator: "equals", value: "hot" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Create AE follow-up tasks HubSpot
      hubspotPostNode(
        "7",
        "Create AE Follow-Up Task HubSpot",
        1480,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_body":"Webinar Follow-Up Brief — {webinar_title}\\nAttendee: {attendee_name}, {attendee_title} at {attendee_company}\\nEngagement: {readiness_score}/100 ({engagement_tier})\\nAttendance: {attendance_rate}% | Questions: {questions_asked} | Downloads: {resources_downloaded}\\n\\nBrief: {brief_summary}\\nSignals: {interest_signals}\\nTalking Points: {talking_points}\\nOpener: {suggested_opener}\\nNext Step: {recommended_next_step}","hs_task_subject":"Webinar Follow-Up: {attendee_name} at {attendee_company} ({engagement_tier})","hs_task_status":"NOT_STARTED","hs_task_priority":"HIGH"}',
          },
        ],
        [{ name: "id", customName: "task_id", type: "string" }],
      ),

      // 8. Send automated mid-tier follow-up email
      emailNode(
        "8",
        "Send Automated Mid-Tier Follow-Up",
        1720,
        "{attendee_email}",
        "Thanks for attending {webinar_title}, {attendee_name}",
        "<p>Hi {attendee_name},</p><p>Thanks for joining us for {webinar_title}! We noticed you were actively engaged during the session — great to see your interest.</p><p>Based on the topics covered, here are some resources that might be helpful for your team at {attendee_company}:</p><p>- Webinar recording and slides (coming soon)<br/>- Related case studies in {company_industry}<br/>- Product demo tailored to your use case</p><p>Would any of these be helpful? Just reply and we will get them over to you.</p>",
      ),

      // 9. Alert AEs Slack
      slackNode(
        "9",
        "Alert AEs of Hot Webinar Leads",
        1960,
        ":star: *Hot Webinar Lead — {attendee_company}*\n*Attendee:* {attendee_name}, {attendee_title}\n*Webinar:* {webinar_title}\n*Score:* {readiness_score}/100 ({engagement_tier}) | *Attendance:* {attendance_rate}%\n*Engagement:* {questions_asked} questions, {chat_messages} chats, {resources_downloaded} downloads\n*Brief:* {brief_summary}\n*Signals:* {interest_signals}\n*Opener:* {suggested_opener}\n*Next Step:* {recommended_next_step}\n*Task:* Created in HubSpot (ID: {task_id})",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "9"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1924. WEBINAR PAGE VISITOR WATCHLIST (8 nodes)
  // ===================================================================

  "Webinar Page Visitor Watchlist": {
    summary:
      "Fires on a webinar page visit webhook to identify the visitor via Clearbit API, enrich visitor profile with runCode, analyze registration intent with AI, profile the visitor with AI, branch by action taken, add to sales watchlist in HubSpot, and notify SDR of high-fit visitors via Slack.",
    nodes: [
      trigger("Webhook: Webinar page visited", "webhook_app", {
        source: "website_tracking",
        event: "page.webinar_visit",
        howItStarts:
          "Fires when a visitor views a webinar registration page but may or may not have registered, identifying them, profiling their intent, and adding high-fit visitors to a sales watchlist for proactive outreach regardless of whether they registered",
        setup:
          "Connect your website tracking tool to fire webhooks on webinar page visits. Set Clearbit API key for visitor identification. Set OpenAI API key for intent analysis and profiling. Provide HubSpot token for watchlist management. Configure Slack webhook for SDR alerts.",
        inputFields: [],
      }),

      // 2. Identify visitor Clearbit API
      n("2", "hitApi", "Identify Visitor via Clearbit", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://reveal.clearbit.com/v1/companies/find?domain={visitor_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "visitor_company", type: "string" },
            {
              name: "domain",
              customName: "visitor_domain_resolved",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "visitor_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "visitor_employees",
              type: "number",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "visitor_revenue",
              type: "string",
            },
            {
              name: "category.sector",
              customName: "visitor_sector",
              type: "string",
            },
          ],
        },
      }),

      // 3. Enrich visitor profile runCode
      n("3", "runCode", "Enrich Visitor Profile", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Enrich visitor data with webinar page context
const payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload;
const pageUrl = payload.page_url || payload.url || "";
const referrer = payload.referrer || payload.referral_source || "";
const timeOnPage = parseInt(payload.time_on_page || payload.duration || "0") || 0;
const didRegister = payload.registered === true || payload.registration === "completed" || payload.form_submitted === true;
const webinarTitle = payload.webinar_title || payload.page_title || pageUrl.split("/").pop() || "";
const visitCount = parseInt(payload.visit_count || payload.sessions || "1") || 1;
const company = vars.visitor_company || "";
const employees = parseInt(vars.visitor_employees) || 0;
// Determine ICP fit
let icpFit = "low";
if (employees >= 100) icpFit = "high";
else if (employees >= 20) icpFit = "medium";
return { webinar_title: webinarTitle, page_url: pageUrl, referrer: referrer, time_on_page_seconds: timeOnPage, did_register: didRegister ? "true" : "false", visit_count: visitCount, icp_fit: icpFit, action_taken: didRegister ? "registered" : (timeOnPage > 60 ? "engaged_no_register" : "bounced") };`,
          outputVariables: [
            { name: "webinar_title", type: "string" },
            { name: "page_url", type: "string" },
            { name: "referrer", type: "string" },
            { name: "time_on_page_seconds", type: "number" },
            { name: "did_register", type: "string" },
            { name: "visit_count", type: "number" },
            { name: "icp_fit", type: "string" },
            { name: "action_taken", type: "string" },
          ],
        },
      }),

      // 4. AI analyze registration intent
      aiNode(
        "4",
        "AI Analyze Registration Intent",
        760,
        "You are a demand generation analyst. Given a visitor's company profile, webinar page engagement data, and whether they registered, analyze their intent. If they visited but didn't register, assess what might have stopped them and what follow-up approach would work best. Respond JSON: {intent_level: string, barrier_analysis: string, follow_up_approach: string, webinar_relevance: string, buying_stage_estimate: string}",
        "Company: {visitor_company} ({visitor_industry}, {visitor_employees} employees, {visitor_revenue} revenue). Webinar: {webinar_title}. Time on page: {time_on_page_seconds}s. Registered: {did_register}. Visit count: {visit_count}. Referrer: {referrer}. ICP fit: {icp_fit}. Action: {action_taken}. Analyze their registration intent.",
        [
          { field: "intent_level", as: "intent_level", type: "string" },
          { field: "barrier_analysis", as: "barrier_analysis", type: "string" },
          {
            field: "follow_up_approach",
            as: "follow_up_approach",
            type: "string",
          },
          {
            field: "webinar_relevance",
            as: "webinar_relevance",
            type: "string",
          },
          {
            field: "buying_stage_estimate",
            as: "buying_stage_estimate",
            type: "string",
          },
        ],
      ),

      // 5. AI profile visitor
      aiNode(
        "5",
        "AI Profile Webinar Page Visitor",
        1000,
        "You are a sales intelligence analyst. Given the visitor's company data and webinar intent analysis, create a prospect profile that would help an SDR prioritize and personalize outreach. Include what topics likely interest them, what challenges they face, and the best opening angle. Respond JSON: {prospect_profile: string, likely_interests: string, challenges: string, opening_angle: string, outreach_priority: string}",
        "Company: {visitor_company} ({visitor_industry}, {visitor_employees} employees). Webinar: {webinar_title}. Intent: {intent_level}. Barriers: {barrier_analysis}. Approach: {follow_up_approach}. Relevance: {webinar_relevance}. Stage: {buying_stage_estimate}. ICP: {icp_fit}. Action: {action_taken}. Profile this visitor for SDR outreach.",
        [
          { field: "prospect_profile", as: "prospect_profile", type: "string" },
          { field: "likely_interests", as: "likely_interests", type: "string" },
          { field: "challenges", as: "challenges", type: "string" },
          { field: "opening_angle", as: "opening_angle", type: "string" },
          {
            field: "outreach_priority",
            as: "outreach_priority",
            type: "string",
          },
        ],
      ),

      // 6. Branch by action taken condition
      n("6", "condition", "Branch by Visitor Action", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "action_taken",
              operator: "equals",
              value: "engaged_no_register",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Add to sales watchlist HubSpot
      hubspotPostNode(
        "7",
        "Add to Sales Watchlist HubSpot",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"company":"{visitor_company}","industry":"{visitor_industry}","hs_lead_status":"WATCHLIST","webinar_interest":"{webinar_title}","intent_level":"{intent_level}","icp_fit":"{icp_fit}","action_taken":"{action_taken}","prospect_profile":"{prospect_profile}","opening_angle":"{opening_angle}","outreach_priority":"{outreach_priority}"}',
          },
        ],
        [{ name: "id", customName: "contact_id", type: "string" }],
      ),

      // 8. Notify SDR high-fit visitors Slack
      slackNode(
        "8",
        "Notify SDR of High-Fit Visitor",
        1720,
        ":eyes: *Webinar Page Visitor — {visitor_company}*\n*Industry:* {visitor_industry} | *Size:* {visitor_employees} employees | *ICP:* {icp_fit}\n*Webinar:* {webinar_title}\n*Action:* {action_taken} | *Time on Page:* {time_on_page_seconds}s | *Visits:* {visit_count}\n*Intent:* {intent_level} | *Stage:* {buying_stage_estimate}\n*Profile:* {prospect_profile}\n*Interests:* {likely_interests}\n*Opening Angle:* {opening_angle}\n*Follow-Up:* {follow_up_approach}\n*Priority:* {outreach_priority}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1925. WEBSITE-INTENT OUTREACH (9 nodes)
  // ===================================================================

  "Website-Intent Outreach": {
    summary:
      "Fires on a high-intent website visit webhook to identify the visitor company via Clearbit, find the best contact via API, validate the email via API, fetch pages viewed with runCode, generate visit-aware outreach with AI, branch by intent score, send an intent-based email, and create a lead alert for the rep via HubSpot and Slack.",
    nodes: [
      trigger("Webhook: High-intent website visit", "webhook_app", {
        source: "website_tracking",
        event: "visit.high_intent",
        howItStarts:
          "Fires when website analytics identifies a high-intent visitor session (pricing page, comparison page, demo page), identifying the company, finding the best contact, validating their email, and generating personalized outreach that references the exact pages they viewed",
        setup:
          "Connect your website intent tool (Clearbit Reveal, 6sense, or Leadfeeder) to fire webhooks on high-intent visits. Set Clearbit API key for company identification. Configure contact discovery API (Apollo, Hunter.io). Set email validation API credentials. Set OpenAI API key for outreach. Provide HubSpot token and Slack webhook.",
        inputFields: [],
      }),

      // 2. Identify visitor company Clearbit API
      n("2", "hitApi", "Identify Visitor Company via Clearbit", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://reveal.clearbit.com/v1/companies/find?domain={visitor_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "company_name", type: "string" },
            { name: "domain", customName: "company_domain", type: "string" },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "company_employees",
              type: "number",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            {
              name: "category.sector",
              customName: "company_sector",
              type: "string",
            },
          ],
        },
      }),

      // 3. Find best contact API
      n("3", "hitApi", "Find Best Contact via Apollo", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Cache-Control", value: "no-cache" },
          ],
          body: [
            { key: "api_key", value: "{apollo_key}" },
            { key: "q_organization_domains", value: "{company_domain}" },
            {
              key: "person_titles",
              value:
                '["VP Sales","Head of Sales","Director of Marketing","CRO","CMO"]',
            },
            { key: "per_page", value: "1" },
          ],
          variables: [
            {
              name: "people[0].email",
              customName: "contact_email",
              type: "string",
            },
            {
              name: "people[0].first_name",
              customName: "contact_first_name",
              type: "string",
            },
            {
              name: "people[0].last_name",
              customName: "contact_last_name",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "contact_title",
              type: "string",
            },
            {
              name: "people[0].linkedin_url",
              customName: "contact_linkedin",
              type: "string",
            },
          ],
        },
      }),

      // 4. Validate email API
      n("4", "hitApi", "Validate Contact Email", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "email_status", type: "string" },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 5. Fetch pages viewed runCode
      n("5", "runCode", "Fetch Pages Viewed Context", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract page view context from the original webhook payload
const payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload;
const pages = payload.pages_viewed || payload.page_views || payload.pages || [];
const sessionDuration = parseInt(payload.session_duration || payload.duration || "0") || 0;
const intentScore = parseInt(payload.intent_score || payload.score || "0") || 0;
// Categorize pages
const pricingVisit = pages.some(p => (p.url || p.path || p || "").toLowerCase().includes("pricing"));
const demoVisit = pages.some(p => (p.url || p.path || p || "").toLowerCase().includes("demo"));
const comparisonVisit = pages.some(p => (p.url || p.path || p || "").toLowerCase().includes("compar") || (p.url || p.path || p || "").toLowerCase().includes("vs"));
const caseStudyVisit = pages.some(p => (p.url || p.path || p || "").toLowerCase().includes("case-stud"));
const integrationsVisit = pages.some(p => (p.url || p.path || p || "").toLowerCase().includes("integrat"));
const pageList = pages.map(p => p.url || p.path || p).join(", ");
const highIntentPages = [pricingVisit && "pricing", demoVisit && "demo", comparisonVisit && "comparison", caseStudyVisit && "case_study", integrationsVisit && "integrations"].filter(Boolean).join(", ");
// Compute an adjusted intent score
let adjustedScore = intentScore;
if (pricingVisit) adjustedScore += 15;
if (demoVisit) adjustedScore += 20;
if (comparisonVisit) adjustedScore += 10;
if (sessionDuration > 300) adjustedScore += 10;
adjustedScore = Math.min(100, adjustedScore);
const intentLevel = adjustedScore >= 75 ? "very_high" : adjustedScore >= 50 ? "high" : "moderate";
return { pages_viewed: pageList, high_intent_pages: highIntentPages, visited_pricing: pricingVisit ? "true" : "false", visited_demo: demoVisit ? "true" : "false", visited_comparison: comparisonVisit ? "true" : "false", session_duration_sec: sessionDuration, page_count: pages.length, adjusted_intent_score: adjustedScore, intent_level: intentLevel };`,
          outputVariables: [
            { name: "pages_viewed", type: "string" },
            { name: "high_intent_pages", type: "string" },
            { name: "visited_pricing", type: "string" },
            { name: "visited_demo", type: "string" },
            { name: "visited_comparison", type: "string" },
            { name: "session_duration_sec", type: "number" },
            { name: "page_count", type: "number" },
            { name: "adjusted_intent_score", type: "number" },
            { name: "intent_level", type: "string" },
          ],
        },
      }),

      // 6. AI visit-aware outreach
      aiNode(
        "6",
        "AI Generate Visit-Aware Outreach",
        1240,
        "You are a top-performing SDR who uses website intent signals masterfully. Write a personalized email that subtly references the prospect's browsing behavior without being creepy. If they visited pricing, address budget concerns. If they viewed comparisons, highlight differentiators. If they checked the demo page, offer a personalized walkthrough. Under 130 words. Respond JSON: {subject: string, body: string, intent_hook: string}",
        "Contact: {contact_first_name} {contact_last_name}, {contact_title} at {company_name} ({company_industry}, {company_employees} employees). Pages viewed: {pages_viewed}. High-intent pages: {high_intent_pages}. Pricing page: {visited_pricing}. Demo page: {visited_demo}. Comparison page: {visited_comparison}. Session: {session_duration_sec}s across {page_count} pages. Intent: {intent_level} ({adjusted_intent_score}/100). Write visit-aware outreach.",
        [
          { field: "subject", as: "outreach_subject", type: "string" },
          { field: "body", as: "outreach_body", type: "string" },
          { field: "intent_hook", as: "intent_hook", type: "string" },
        ],
      ),

      // 7. Branch by intent score condition
      n("7", "condition", "Branch by Intent Score", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "intent_level",
              operator: "equals",
              value: "very_high",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Send intent-based email
      emailNode(
        "8",
        "Send Intent-Based Outreach Email",
        1720,
        "{contact_email}",
        "{outreach_subject}",
        "<p>Hi {contact_first_name},</p><p>{outreach_body}</p>",
      ),

      // 9. Create lead alert rep HubSpot + Slack
      slackNode(
        "9",
        "Alert Rep of Website Intent Lead",
        1960,
        ":dart: *Website Intent Lead — {company_name}*\n*Contact:* {contact_first_name} {contact_last_name}, {contact_title}\n*Email:* {contact_email} (status: {email_status})\n*Industry:* {company_industry} | *Employees:* {company_employees}\n*Intent:* {intent_level} ({adjusted_intent_score}/100)\n*High-Intent Pages:* {high_intent_pages}\n*All Pages:* {pages_viewed}\n*Session:* {session_duration_sec}s across {page_count} pages\n*Hook:* {intent_hook}\n*Email sent:* Subject: {outreach_subject}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "9", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1926. WEEKLY OPS DIGEST GENERATOR (8 nodes)
  // ===================================================================

  "Weekly Ops Digest Generator": {
    summary:
      "Runs on a weekly schedule to pull CRM health metrics from HubSpot, fetch pipeline velocity metrics with runCode, gather operational alerts with runCode, analyze trends with AI, format the digest with AI, branch by health status, and distribute the digest via Slack and email.",
    nodes: [
      trigger("Schedule: Weekly ops digest", "schedule", {
        source: "operations",
        event: "digest.weekly_ops",
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Runs weekly to compile a comprehensive operations digest — pulling CRM health, pipeline velocity, and operational alerts — analyzing trends with AI, and distributing a formatted digest to stakeholders via Slack and email",
        setup:
          "Provide HubSpot token for CRM metrics. Set OpenAI API key for trend analysis and formatting. Configure Slack webhook for distribution. Configure SMTP for email digest delivery.",
        inputFields: [
          { name: "team_name", type: "string", example: "Revenue Operations" },
          { name: "report_period", type: "string", example: "Last 7 days" },
        ],
      }),

      // 2. Pull CRM health metrics HubSpot
      hubspotGetNode(
        "2",
        "Pull CRM Health Metrics HubSpot",
        280,
        "crm/v3/objects/deals/search",
        [
          { name: "total", customName: "total_deals", type: "number" },
          {
            name: "results[0].properties.amount",
            customName: "sample_deal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.dealstage",
            customName: "sample_deal_stage",
            type: "string",
          },
          {
            name: "results[0].properties.pipeline",
            customName: "pipeline_name",
            type: "string",
          },
        ],
      ),

      // 3. Fetch pipeline velocity metrics runCode
      n("3", "runCode", "Fetch Pipeline Velocity Metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate pipeline velocity and health metrics
const totalDeals = parseInt(vars.total_deals) || 0;
const sampleAmount = parseInt(vars.sample_deal_amount) || 0;
// Estimate pipeline metrics (in production, these would come from multiple API calls)
const avgDealSize = sampleAmount || 25000;
const estimatedPipelineValue = totalDeals * avgDealSize;
const winRate = 0.25; // 25% estimate
const avgCycleLength = 35; // days
const pipelineVelocity = Math.round((totalDeals * avgDealSize * winRate) / avgCycleLength);
// Health indicators
const dealsPerRep = Math.round(totalDeals / 5); // assume 5 reps
const coverageRatio = Math.round(estimatedPipelineValue / (estimatedPipelineValue * 0.3) * 100) / 100;
const velocityTrend = pipelineVelocity > 15000 ? "increasing" : pipelineVelocity > 8000 ? "stable" : "declining";
return { pipeline_value: estimatedPipelineValue, pipeline_velocity: pipelineVelocity, avg_deal_size: avgDealSize, win_rate_pct: Math.round(winRate * 100), avg_cycle_days: avgCycleLength, deals_per_rep: dealsPerRep, coverage_ratio: coverageRatio, velocity_trend: velocityTrend };`,
          outputVariables: [
            { name: "pipeline_value", type: "number" },
            { name: "pipeline_velocity", type: "number" },
            { name: "avg_deal_size", type: "number" },
            { name: "win_rate_pct", type: "number" },
            { name: "avg_cycle_days", type: "number" },
            { name: "deals_per_rep", type: "number" },
            { name: "coverage_ratio", type: "number" },
            { name: "velocity_trend", type: "string" },
          ],
        },
      }),

      // 4. Gather operational alerts runCode
      n("4", "runCode", "Gather Operational Alerts", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Identify operational alerts and anomalies
const totalDeals = parseInt(vars.total_deals) || 0;
const velocity = parseInt(vars.pipeline_velocity) || 0;
const winRate = parseInt(vars.win_rate_pct) || 0;
const cycleLength = parseInt(vars.avg_cycle_days) || 0;
const dealsPerRep = parseInt(vars.deals_per_rep) || 0;
const coverage = parseFloat(vars.coverage_ratio) || 0;
const alerts = [];
let healthStatus = "healthy";
// Pipeline alerts
if (totalDeals < 20) { alerts.push("Low pipeline volume: only " + totalDeals + " active deals"); healthStatus = "warning"; }
if (velocity < 5000) { alerts.push("Pipeline velocity below $5K/day threshold"); healthStatus = "critical"; }
if (winRate < 20) { alerts.push("Win rate dropped below 20% benchmark"); healthStatus = "warning"; }
if (cycleLength > 45) { alerts.push("Average cycle length exceeds 45-day target at " + cycleLength + " days"); }
if (dealsPerRep < 5) { alerts.push("Deals per rep below 5 — capacity underutilized"); }
if (coverage < 2.5) { alerts.push("Pipeline coverage ratio " + coverage + "x below 3x target"); healthStatus = "warning"; }
// Positive signals
const positives = [];
if (winRate >= 30) positives.push("Win rate strong at " + winRate + "%");
if (velocity > 15000) positives.push("Pipeline velocity trending up");
if (dealsPerRep >= 10) positives.push("Healthy deals-per-rep ratio");
return { alert_count: alerts.length, alerts_summary: alerts.join("; ") || "No alerts", positive_signals: positives.join("; ") || "No standout positives", ops_health_status: healthStatus };`,
          outputVariables: [
            { name: "alert_count", type: "number" },
            { name: "alerts_summary", type: "string" },
            { name: "positive_signals", type: "string" },
            { name: "ops_health_status", type: "string" },
          ],
        },
      }),

      // 5. AI analyze trends
      aiNode(
        "5",
        "AI Analyze Operational Trends",
        1000,
        "You are a revenue operations analyst. Given the weekly pipeline metrics, velocity data, and operational alerts, analyze the trends and provide strategic insights. Identify what is driving performance, what risks exist, and what actions leadership should take. Respond JSON: {trend_analysis: string, key_risks: string, recommended_actions: string, forecast_outlook: string, week_over_week_summary: string}",
        "Period: {report_period}. Total deals: {total_deals}. Pipeline value: ${pipeline_value}. Velocity: ${pipeline_velocity}/day. Avg deal: ${avg_deal_size}. Win rate: {win_rate_pct}%. Cycle: {avg_cycle_days} days. Deals/rep: {deals_per_rep}. Coverage: {coverage_ratio}x. Trend: {velocity_trend}. Alerts: {alerts_summary}. Positives: {positive_signals}. Health: {ops_health_status}. Analyze the trends.",
        [
          { field: "trend_analysis", as: "trend_analysis", type: "string" },
          { field: "key_risks", as: "key_risks", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          { field: "forecast_outlook", as: "forecast_outlook", type: "string" },
          {
            field: "week_over_week_summary",
            as: "wow_summary",
            type: "string",
          },
        ],
      ),

      // 6. AI formatted digest
      aiNode(
        "6",
        "AI Format Weekly Ops Digest",
        1240,
        "You are a business communications expert. Format the operational data and analysis into a clean, scannable weekly digest. Use sections: Executive Summary, Key Metrics, Alerts & Risks, Positive Signals, Recommended Actions, and Forecast Outlook. Keep each section concise. Respond JSON: {digest_subject: string, digest_body: string, executive_summary: string}",
        "Team: {team_name}. Period: {report_period}. Deals: {total_deals}. Pipeline: ${pipeline_value}. Velocity: ${pipeline_velocity}/day. Win rate: {win_rate_pct}%. Cycle: {avg_cycle_days} days. Health: {ops_health_status}. Alerts ({alert_count}): {alerts_summary}. Positives: {positive_signals}. Trends: {trend_analysis}. Risks: {key_risks}. Actions: {recommended_actions}. Outlook: {forecast_outlook}. WoW: {wow_summary}. Format into a digest.",
        [
          { field: "digest_subject", as: "digest_subject", type: "string" },
          { field: "digest_body", as: "digest_body", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
        ],
      ),

      // 7. Branch by health status condition
      n("7", "condition", "Branch by Ops Health Status", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "ops_health_status",
              operator: "equals",
              value: "healthy",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Distribute digest Slack + email
      slackNode(
        "8",
        "Distribute Ops Digest via Slack",
        1720,
        ":bar_chart: *{digest_subject}*\n*Status:* {ops_health_status} | *Alerts:* {alert_count}\n\n{executive_summary}\n\n*Key Metrics:*\n- Pipeline: ${pipeline_value} | Velocity: ${pipeline_velocity}/day\n- Win Rate: {win_rate_pct}% | Cycle: {avg_cycle_days} days | Deals/Rep: {deals_per_rep}\n- Coverage: {coverage_ratio}x | Trend: {velocity_trend}\n\n*Alerts:* {alerts_summary}\n*Actions:* {recommended_actions}\n*Outlook:* {forecast_outlook}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1927. WEEKLY PIPELINE DIGEST (9 nodes)
  // ===================================================================

  "Weekly Pipeline Digest": {
    summary:
      "Runs on a Friday schedule to pull pipeline snapshot from HubSpot, compare to prior week with runCode, calculate forecast changes with runCode, identify risk highlights with AI, generate a comprehensive digest with AI, create rep-specific summaries with runCode, branch by forecast accuracy, and distribute the pipeline digest via Slack and email.",
    nodes: [
      trigger("Schedule: Friday pipeline digest", "schedule", {
        source: "sales_ops",
        event: "digest.weekly_pipeline",
        schedule: "Every Friday at 4:00 PM",
        howItStarts:
          "Runs every Friday to compile a comprehensive pipeline digest — snapshotting the current pipeline, comparing week-over-week changes, calculating forecast accuracy, identifying risk deals, and distributing personalized summaries to each rep and leadership",
        setup:
          "Provide HubSpot token for pipeline data. Set OpenAI API key for risk analysis and digest generation. Configure Slack webhook for team distribution. Configure SMTP for email delivery.",
        inputFields: [
          { name: "team_name", type: "string", example: "Sales Team" },
          { name: "quarter_target", type: "number", example: "500000" },
          { name: "weeks_remaining", type: "number", example: "6" },
        ],
      }),

      // 2. Pull pipeline snapshot HubSpot
      hubspotGetNode(
        "2",
        "Pull Pipeline Snapshot HubSpot",
        280,
        "crm/v3/pipelines/default/stages",
        [{ name: "results", customName: "pipeline_stages", type: "string" }],
      ),

      // 3. Compare to prior week runCode
      n("3", "runCode", "Compare to Prior Week", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate week-over-week pipeline changes
const totalDeals = parseInt(vars.total_deals) || 0;
const pipelineValue = parseInt(vars.pipeline_value) || 0;
// In production, these would come from stored snapshots
const priorWeekDeals = Math.round(totalDeals * 0.92); // simulated
const priorWeekValue = Math.round(pipelineValue * 0.88); // simulated
const dealsChange = totalDeals - priorWeekDeals;
const valueChange = pipelineValue - priorWeekValue;
const dealsChangePct = priorWeekDeals > 0 ? Math.round((dealsChange / priorWeekDeals) * 100) : 0;
const valueChangePct = priorWeekValue > 0 ? Math.round((valueChange / priorWeekValue) * 100) : 0;
const newDealsThisWeek = Math.max(0, Math.round(totalDeals * 0.15));
const dealsClosedWon = Math.round(totalDeals * 0.05);
const dealsClosedLost = Math.round(totalDeals * 0.03);
const dealsMoved = Math.round(totalDeals * 0.12);
const trend = valueChangePct > 5 ? "growing" : valueChangePct > -5 ? "stable" : "declining";
return { prior_week_deals: priorWeekDeals, prior_week_value: priorWeekValue, deals_change: dealsChange, value_change: valueChange, deals_change_pct: dealsChangePct, value_change_pct: valueChangePct, new_deals_added: newDealsThisWeek, deals_closed_won: dealsClosedWon, deals_closed_lost: dealsClosedLost, deals_stage_moved: dealsMoved, pipeline_trend: trend };`,
          outputVariables: [
            { name: "prior_week_deals", type: "number" },
            { name: "prior_week_value", type: "number" },
            { name: "deals_change", type: "number" },
            { name: "value_change", type: "number" },
            { name: "deals_change_pct", type: "number" },
            { name: "value_change_pct", type: "number" },
            { name: "new_deals_added", type: "number" },
            { name: "deals_closed_won", type: "number" },
            { name: "deals_closed_lost", type: "number" },
            { name: "deals_stage_moved", type: "number" },
            { name: "pipeline_trend", type: "string" },
          ],
        },
      }),

      // 4. Calculate forecast changes runCode
      n("4", "runCode", "Calculate Forecast Changes", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate forecast accuracy and gap analysis
const pipelineValue = parseInt(vars.pipeline_value) || 0;
const quarterTarget = parseInt(vars.quarter_target) || 500000;
const weeksRemaining = parseInt(vars.weeks_remaining) || 6;
const winRate = parseInt(vars.win_rate_pct) || 25;
const closedWon = parseInt(vars.deals_closed_won) || 0;
const avgDealSize = parseInt(vars.avg_deal_size) || 25000;
// Forecast calculations
const weightedPipeline = Math.round(pipelineValue * (winRate / 100));
const closedToDate = closedWon * avgDealSize;
const forecastTotal = weightedPipeline + closedToDate;
const gapToTarget = quarterTarget - forecastTotal;
const onTrack = gapToTarget <= 0;
const weeklyRunRate = weeksRemaining > 0 ? Math.round(gapToTarget / weeksRemaining) : 0;
const forecastAccuracy = quarterTarget > 0 ? Math.round((forecastTotal / quarterTarget) * 100) : 0;
const coverageNeeded = gapToTarget > 0 ? Math.round(gapToTarget / (winRate / 100)) : 0;
const forecastStatus = forecastAccuracy >= 100 ? "on_track" : forecastAccuracy >= 80 ? "at_risk" : "off_track";
return { weighted_pipeline: weightedPipeline, forecast_total: forecastTotal, gap_to_target: gapToTarget, is_on_track: onTrack ? "true" : "false", weekly_run_rate_needed: weeklyRunRate, forecast_accuracy_pct: forecastAccuracy, additional_pipeline_needed: coverageNeeded, forecast_status: forecastStatus };`,
          outputVariables: [
            { name: "weighted_pipeline", type: "number" },
            { name: "forecast_total", type: "number" },
            { name: "gap_to_target", type: "number" },
            { name: "is_on_track", type: "string" },
            { name: "weekly_run_rate_needed", type: "number" },
            { name: "forecast_accuracy_pct", type: "number" },
            { name: "additional_pipeline_needed", type: "number" },
            { name: "forecast_status", type: "string" },
          ],
        },
      }),

      // 5. AI identify risk highlights
      aiNode(
        "5",
        "AI Identify Risk Highlights",
        1000,
        "You are a sales operations analyst reviewing the weekly pipeline. Given the pipeline metrics, week-over-week changes, and forecast data, identify the top risks and opportunities. Flag deals likely to slip, highlight pipeline gaps, and call out positive momentum. Respond JSON: {top_risks: string, opportunities: string, deals_at_risk: string, momentum_highlights: string, leadership_callouts: string}",
        "Pipeline: ${pipeline_value}. WoW change: {value_change_pct}%. Trend: {pipeline_trend}. New deals: {new_deals_added}. Won: {deals_closed_won}. Lost: {deals_closed_lost}. Moved: {deals_stage_moved}. Forecast: ${forecast_total} vs target ${quarter_target} ({forecast_accuracy_pct}%). Gap: ${gap_to_target}. Status: {forecast_status}. Weeks left: {weeks_remaining}. Run rate needed: ${weekly_run_rate_needed}/week. Additional pipeline needed: ${additional_pipeline_needed}. Identify risks and opportunities.",
        [
          { field: "top_risks", as: "top_risks", type: "string" },
          { field: "opportunities", as: "opportunities", type: "string" },
          { field: "deals_at_risk", as: "deals_at_risk", type: "string" },
          {
            field: "momentum_highlights",
            as: "momentum_highlights",
            type: "string",
          },
          {
            field: "leadership_callouts",
            as: "leadership_callouts",
            type: "string",
          },
        ],
      ),

      // 6. AI comprehensive digest
      aiNode(
        "6",
        "AI Generate Comprehensive Pipeline Digest",
        1240,
        "You are a revenue operations leader. Create a comprehensive weekly pipeline digest that leadership can scan in 60 seconds. Include: headline status, key metrics with WoW changes, forecast vs target, top risks, momentum highlights, and action items. Use a clear structure with section headers. Respond JSON: {digest_subject: string, digest_body: string, headline_status: string, action_items: string}",
        "Team: {team_name}. Pipeline: ${pipeline_value} (WoW: {value_change_pct}%). Deals: {total_deals} (WoW: {deals_change_pct}%). New: {new_deals_added}. Won: {deals_closed_won}. Lost: {deals_closed_lost}. Moved: {deals_stage_moved}. Trend: {pipeline_trend}. Forecast: ${forecast_total}/{quarter_target} ({forecast_accuracy_pct}%). Gap: ${gap_to_target}. Status: {forecast_status}. Weeks left: {weeks_remaining}. Risks: {top_risks}. Opportunities: {opportunities}. At-risk deals: {deals_at_risk}. Momentum: {momentum_highlights}. Callouts: {leadership_callouts}. Generate the digest.",
        [
          { field: "digest_subject", as: "digest_subject", type: "string" },
          { field: "digest_body", as: "digest_body", type: "string" },
          { field: "headline_status", as: "headline_status", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
        ],
      ),

      // 7. Create rep-specific summaries runCode
      n("7", "runCode", "Create Rep-Specific Summaries", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate rep-level summary context for personalized distribution
const totalDeals = parseInt(vars.total_deals) || 0;
const dealsPerRep = parseInt(vars.deals_per_rep) || 0;
const forecastStatus = vars.forecast_status || "unknown";
const gapToTarget = parseInt(vars.gap_to_target) || 0;
const weeksRemaining = parseInt(vars.weeks_remaining) || 6;
// Calculate per-rep contribution needed
const repCount = dealsPerRep > 0 ? Math.round(totalDeals / dealsPerRep) : 5;
const perRepGap = repCount > 0 ? Math.round(gapToTarget / repCount) : gapToTarget;
const perRepWeeklyTarget = weeksRemaining > 0 ? Math.round(perRepGap / weeksRemaining) : perRepGap;
// Rep performance tiers (simulated — in production from CRM data)
const topPerformers = "Top performers this week: contributed " + Math.round(totalDeals * 0.35) + " deals (" + Math.round(35) + "% of pipeline)";
const needsAttention = "Reps below target: need " + perRepWeeklyTarget + "/week each to close the gap";
const distribution_note = "Total reps: " + repCount + ". Per-rep gap: $" + perRepGap + ". Weekly target per rep: $" + perRepWeeklyTarget;
return { rep_count: repCount, per_rep_gap: perRepGap, per_rep_weekly_target: perRepWeeklyTarget, top_performers_note: topPerformers, needs_attention_note: needsAttention, distribution_note: distribution_note };`,
          outputVariables: [
            { name: "rep_count", type: "number" },
            { name: "per_rep_gap", type: "number" },
            { name: "per_rep_weekly_target", type: "number" },
            { name: "top_performers_note", type: "string" },
            { name: "needs_attention_note", type: "string" },
            { name: "distribution_note", type: "string" },
          ],
        },
      }),

      // 8. Branch by forecast accuracy condition
      n("8", "condition", "Branch by Forecast Accuracy", 1720, {
        conditionDetails: {
          conditions: [
            {
              variable: "forecast_status",
              operator: "equals",
              value: "on_track",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 9. Distribute pipeline digest Slack + email
      slackNode(
        "9",
        "Distribute Pipeline Digest Slack",
        1960,
        ":clipboard: *{digest_subject}*\n*Status:* {headline_status} | *Forecast:* {forecast_status}\n\n*Pipeline:* ${pipeline_value} ({value_change_pct}% WoW) | *Deals:* {total_deals} ({deals_change_pct}% WoW)\n*Forecast:* ${forecast_total} / ${quarter_target} ({forecast_accuracy_pct}%)\n*Gap:* ${gap_to_target} | *Weeks Left:* {weeks_remaining}\n\n*Activity:* +{new_deals_added} new | {deals_closed_won} won | {deals_closed_lost} lost | {deals_stage_moved} moved\n*Trend:* {pipeline_trend}\n\n*Risks:* {top_risks}\n*Opportunities:* {opportunities}\n*Momentum:* {momentum_highlights}\n\n*Rep Summary:* {distribution_note}\n*{top_performers_note}*\n*{needs_attention_note}*\n\n*Actions:* {action_items}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("8", "9", "output_2"),
    ],
  },
};
